import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import { Head, router } from "@inertiajs/react";
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    CheckCircle2,
    Clock,
    AlertTriangle,
    GitBranch,
    Users,
    Activity,
    Calendar,
    Filter,
    Search,
    ChevronUp,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    BarChart3,
    PieChart as PieChartIcon,
    Check,
} from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    AreaChart,
    Area,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { DatePickerWithRange } from "@/components/date-range-picker";
import { useMediaQuery } from "@/hooks/use-mobile";
import {
    Command,
    CommandGroup,
    CommandItem,
} from "@/components/ui/command";
import { useTasks, TaskProvider } from "@/lib/contexts/task-context";
import { format } from "date-fns";
import { useUsers } from "@/lib/contexts/user-context";
import { useWorkflow } from "@/lib/contexts/workflow-context";
import { WorkflowProvider } from "@/lib/contexts/workflow-context";
import { TaskDetailModal } from "@/components/tasks/task-detail-modal";

export default function DashboardPage({ tasks, task, date, categories, statuses, users: backendUsers, departments, permissions, csrf_token }) {
    const { currentUser: contextCurrentUser } = useUsers();
    // Use backendUsers instead of users from context to ensure we have the correct data
    const users = backendUsers || [];
    
    // Merge currentUser with backend user data to get department_id and other fields
    const currentUser = useMemo(() => {
        if (!contextCurrentUser?.id) return contextCurrentUser;
        const backendUser = users.find(u => u.id === contextCurrentUser.id);
        if (backendUser) {
            return {
                ...contextCurrentUser,
                department_id: backendUser.department_id,
                role: backendUser.role,
            };
        }
        return contextCurrentUser;
    }, [contextCurrentUser, users]);

    // Load viewMode from localStorage
    const loadViewMode = () => {
        try {
            const saved = localStorage.getItem('dashboard-view-mode');
            return saved || 'personal';
        } catch (error) {
            console.error('Error loading viewMode:', error);
            return 'personal';
        }
    };

    // Save viewMode to localStorage
    const saveViewMode = (mode) => {
        try {
            localStorage.setItem('dashboard-view-mode', mode);
        } catch (error) {
            console.error('Error saving viewMode:', error);
        }
    };

    const [viewMode, setViewMode] = useState(loadViewMode());
    
    // Track viewMode changes to prevent saving filters during view switch
    const previousViewModeRef = useRef(viewMode);
    const isInitialMountRef = useRef(true);

    // Save viewMode when it changes
    useEffect(() => {
        saveViewMode(viewMode);
    }, [viewMode]);

    // Load saved filters from localStorage for specific view mode
    // Filters expire after 1 hour (3600000 ms)
    const loadSavedFilters = (mode) => {
        const key = mode === 'team' ? 'dashboard-filters-team' : 'dashboard-filters-personal';
        const TTL_MS = 3600000; // 1 hour
        try {
            const savedFilters = localStorage.getItem(key);
            if (savedFilters) {
                const parsed = JSON.parse(savedFilters);
                
                // Check if filters have expiration timestamp
                if (parsed && typeof parsed === 'object' && 'savedAt' in parsed) {
                    const now = new Date().getTime();
                    const savedAt = parsed.savedAt;
                    
                    // Check if expired (older than 1 hour)
                    if (now - savedAt > TTL_MS) {
                        // Expired - remove from storage and return defaults
                        localStorage.removeItem(key);
                        return getDefaultFilters();
                    }
                    
                    // Not expired - update timestamp to reset expiration timer
                    const filters = parsed.filters || parsed; // Support both formats
                    const updatedData = {
                        filters: filters,
                        savedAt: now, // Reset timestamp to current time
                    };
                    localStorage.setItem(key, JSON.stringify(updatedData));
                    
                    return {
                        timeFilter: filters.timeFilter || "week",
                        dateRange: filters.dateRange || { from: undefined, to: undefined },
                        selectedTeamMembers: filters.selectedTeamMembers || [],
                        selectedDepartment: filters.selectedDepartment || null,
                        selectedStatus: filters.selectedStatus || "all",
                        selectedPriority: filters.selectedPriority || "all",
                        selectedCategory: filters.selectedCategory || "all",
                        showFilters: filters.showFilters || false,
                    };
                }
                
                // Legacy format (no timestamp) - treat as expired and remove
                localStorage.removeItem(key);
                return getDefaultFilters();
            }
        } catch (error) {
            console.error('Error loading saved filters:', error);
        }
        
        return getDefaultFilters();
    };

    // Helper function to get default filter values
    const getDefaultFilters = () => {
        return {
            timeFilter: "week",
            dateRange: { from: undefined, to: undefined },
            selectedTeamMembers: [],
            selectedDepartment: null,
            selectedStatus: "all",
            selectedPriority: "all",
            selectedCategory: "all",
            showFilters: false,
        };
    };

    // Save filters to localStorage with expiration timestamp
    // Filters will expire after 2 hours
    const saveFilters = (filters, mode) => {
        const key = mode === 'team' ? 'dashboard-filters-team' : 'dashboard-filters-personal';
        try {
            const filtersToSave = {
                timeFilter: filters.timeFilter !== undefined ? filters.timeFilter : timeFilter,
                dateRange: filters.dateRange !== undefined ? filters.dateRange : dateRange,
                selectedTeamMembers: filters.selectedTeamMembers !== undefined ? filters.selectedTeamMembers : selectedTeamMembers,
                selectedDepartment: filters.selectedDepartment !== undefined ? filters.selectedDepartment : selectedDepartment,
                selectedStatus: filters.selectedStatus !== undefined ? filters.selectedStatus : selectedStatus,
                selectedPriority: filters.selectedPriority !== undefined ? filters.selectedPriority : selectedPriority,
                selectedCategory: filters.selectedCategory !== undefined ? filters.selectedCategory : selectedCategory,
                showFilters: filters.showFilters !== undefined ? filters.showFilters : showFilters,
            };
            
            // Store with timestamp for expiration check
            const dataToStore = {
                filters: filtersToSave,
                savedAt: new Date().getTime(),
            };
            
            localStorage.setItem(key, JSON.stringify(dataToStore));
        } catch (error) {
            console.error('Error saving filters:', error);
        }
    };

    // Load initial filter values based on current viewMode
    const initialFilters = loadSavedFilters(viewMode);
    const [timeFilter, setTimeFilter] = useState(initialFilters.timeFilter);
    const [selectedTeamMembers, setSelectedTeamMembers] = useState(initialFilters.selectedTeamMembers);
    const selectedTeamMembersRef = useRef(selectedTeamMembers);
    
    // Keep ref in sync with state
    useEffect(() => {
        selectedTeamMembersRef.current = selectedTeamMembers;
    }, [selectedTeamMembers]);
    const [selectedDepartment, setSelectedDepartment] = useState(initialFilters.selectedDepartment || null);
    const [selectedStatus, setSelectedStatus] = useState(initialFilters.selectedStatus || "all");
    const [selectedPriority, setSelectedPriority] = useState(initialFilters.selectedPriority || "all");
    const [selectedCategory, setSelectedCategory] = useState(initialFilters.selectedCategory || "all");
    const [showFilters, setShowFilters] = useState(initialFilters.showFilters);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [showTasksDueToday, setShowTasksDueToday] = useState(false);
    const [showOverdueTasks, setShowOverdueTasks] = useState(false);
    const [showCompletedTasks, setShowCompletedTasks] = useState(false);
    const [showActiveWorkflows, setShowActiveWorkflows] = useState(false);

    // Search, filter, and sort state for modals
    const [tasksDueTodaySearchTerm, setTasksDueTodaySearchTerm] = useState("");
    const [tasksDueTodayStatusFilter, setTasksDueTodayStatusFilter] = useState("All");
    const [tasksDueTodayPriorityFilter, setTasksDueTodayPriorityFilter] = useState("All");
    const [tasksDueTodaySortField, setTasksDueTodaySortField] = useState("dueDate");
    const [tasksDueTodaySortDirection, setTasksDueTodaySortDirection] = useState("asc");

    const [overdueTasksSearchTerm, setOverdueTasksSearchTerm] = useState("");
    const [overdueTasksStatusFilter, setOverdueTasksStatusFilter] = useState("All");
    const [overdueTasksPriorityFilter, setOverdueTasksPriorityFilter] = useState("All");
    const [overdueTasksSortField, setOverdueTasksSortField] = useState("dueDate");
    const [overdueTasksSortDirection, setOverdueTasksSortDirection] = useState("asc");

    const [completedTasksSearchTerm, setCompletedTasksSearchTerm] = useState("");
    const [completedTasksStatusFilter, setCompletedTasksStatusFilter] = useState("All");
    const [completedTasksPriorityFilter, setCompletedTasksPriorityFilter] = useState("All");
    const [completedTasksSortField, setCompletedTasksSortField] = useState("completedDate");
    const [completedTasksSortDirection, setCompletedTasksSortDirection] = useState("desc");
    const [showRecentActivityExpanded, setShowRecentActivityExpanded] = useState(false);
    const [showUpcomingTasksExpanded, setShowUpcomingTasksExpanded] = useState(false);
    const [showCompleteTaskDialog, setShowCompleteTaskDialog] = useState(false);
    const [taskToComplete, setTaskToComplete] = useState(null);
    
    // Task detail modal state
    const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
    const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null);
    
    // Overdue tasks table filters
    const [overdueSearchTerm, setOverdueSearchTerm] = useState("");
    const [overduePriorityFilter, setOverduePriorityFilter] = useState("All");
    const [overdueStatusFilter, setOverdueStatusFilter] = useState("All");
    const [overdueDateFilter, setOverdueDateFilter] = useState("All");
    
    // Overdue tasks table sorting
    const [overdueSortField, setOverdueSortField] = useState("id");
    const [overdueSortDirection, setOverdueSortDirection] = useState("asc");
    
    // Overdue tasks pagination
    const [overduePage, setOverduePage] = useState(0);
    const [overdueRowsPerPage, setOverdueRowsPerPage] = useState(10);
    
    // Active tasks modal state
    const [showActiveTasksModal, setShowActiveTasksModal] = useState(false);
    const [activeTasksSearchTerm, setActiveTasksSearchTerm] = useState("");
    const [activeTasksStatusFilter, setActiveTasksStatusFilter] = useState("All");
    const [activeTasksPriorityFilter, setActiveTasksPriorityFilter] = useState("All");
    const [activeTasksPage, setActiveTasksPage] = useState(0);
    const [activeTasksRowsPerPage, setActiveTasksRowsPerPage] = useState(10);
    
    // All tasks modal state (for completion rate card)
    const [showAllTasksModal, setShowAllTasksModal] = useState(false);
    const [allTasksSearchTerm, setAllTasksSearchTerm] = useState("");
    const [allTasksStatusFilter, setAllTasksStatusFilter] = useState("Completed");
    const [allTasksPriorityFilter, setAllTasksPriorityFilter] = useState("All");
    const [allTasksPage, setAllTasksPage] = useState(0);
    const [allTasksRowsPerPage, setAllTasksRowsPerPage] = useState(10);
    
    // Helper function to get current week start (Sunday) and end (Saturday)
    const getCurrentWeekRange = () => {
        const now = new Date();
        const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const daysFromSunday = currentDay; // Convert to Sunday-based week
        
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - daysFromSunday);
        weekStart.setHours(0, 0, 0, 0);
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // Add 6 days to get to Saturday
        weekEnd.setHours(23, 59, 59, 999);
        
        return { from: weekStart, to: weekEnd };
    };

    const getTodayRange = () => {
        const today = new Date();
        const startOfDay = new Date(today);
        startOfDay.setHours(0, 0, 0, 0);
        
        const endOfDay = new Date(today);
        endOfDay.setHours(23, 59, 59, 999);
        
        return { from: startOfDay, to: endOfDay, label: "Today" };
    };

    // Helper function to format employee names for graph display
    const formatEmployeeNameForGraph = (fullName, maxLength = 15) => {
        if (!fullName || typeof fullName !== 'string') {
            return fullName || '';
        }
        
        // Trim and split name into parts
        const nameParts = fullName.trim().split(/\s+/).filter(part => part.length > 0);
        
        // Handle edge cases
        if (nameParts.length === 0) {
            return '';
        }
        
        if (nameParts.length === 1) {
            // Single name - just truncate if needed
            return nameParts[0].length > maxLength 
                ? nameParts[0].substring(0, maxLength - 3) + '...'
                : nameParts[0];
        }
        
        // Multiple parts - format as "J. Smith"
        const firstName = nameParts[0];
        const lastName = nameParts[nameParts.length - 1];
        const firstInitial = firstName.charAt(0).toUpperCase();
        const formattedName = `${firstInitial}. ${lastName}`;
        
        // Truncate if too long
        if (formattedName.length > maxLength) {
            return formattedName.substring(0, maxLength - 3) + '...';
        }
        
        return formattedName;
    };

    const [dateRange, setDateRange] = useState(initialFilters.dateRange.from || initialFilters.dateRange.to ? initialFilters.dateRange : getTodayRange());
    const isMobile = useMediaQuery("(max-width: 640px)");
    const isTablet = useMediaQuery("(max-width: 768px)");

    const { toast } = useToast();
    // const { tasks, activities, getTaskStats } = useTasks();
    // const taskStats = getTaskStats();

    const { workflows } = useWorkflow();
    
    // Check if user has access to team view based on permissions
    const canAccessTeamView = permissions?.can_view_team_view || false;

    // Ensure tasks is always an array
    const safeTasks = Array.isArray(tasks) ? tasks : [];

    // Helper function to get task due date
    const getTaskDueDate = (task) => {
        if (task.defer_date) return new Date(task.defer_date);
        if (task.dueDate) return new Date(task.dueDate);
        if (task.due) return new Date(task.due);
        return null;
    };

    // Helper function to filter tasks based on date range and time period
    const filterTasksByDate = (tasksToFilter) => {
        return tasksToFilter.filter(task => {
            // Handle different date formats from backend
            let taskDate;
            if (task.defer_date) {
                taskDate = new Date(task.defer_date);
            } else if (task.dueDate) {
                taskDate = new Date(task.dueDate);
            } else if (task.due) {
                taskDate = new Date(task.due);
            } else {
                return false; // No due date found
            }

            // Check if the date is valid
            if (isNaN(taskDate.getTime())) {
                return false;
            }

            // If date range is set, use that for filtering (ignore time period)
            if (dateRange.from || dateRange.to) {
                const fromDate = dateRange.from ? new Date(dateRange.from) : null;
                const toDate = dateRange.to ? new Date(dateRange.to) : null;
                
                if (fromDate && taskDate < fromDate) return false;
                if (toDate && taskDate > toDate) return false;
                
                return true;
            }

            // If time period is set (and no date range), use that for filtering
            if (timeFilter && timeFilter !== "week") {
                const now = new Date();
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                            switch (timeFilter) {
                case "day":
                    // Normalize both dates to start of day for comparison
                    const taskDateNormalized = new Date(taskDate);
                    taskDateNormalized.setHours(0, 0, 0, 0);
                    const todayNormalized = new Date(today);
                    todayNormalized.setHours(0, 0, 0, 0);
                    return taskDateNormalized.getTime() === todayNormalized.getTime();
                case "tomorrow":
                    // Normalize both dates to start of day for comparison
                    const taskDateNormalizedTomorrow = new Date(taskDate);
                    taskDateNormalizedTomorrow.setHours(0, 0, 0, 0);
                    const tomorrow = new Date(today);
                    tomorrow.setDate(today.getDate() + 1);
                    tomorrow.setHours(0, 0, 0, 0);
                    return taskDateNormalizedTomorrow.getTime() === tomorrow.getTime();
                case "week":
                    const weekAgo = new Date(today);
                    weekAgo.setDate(today.getDate() - 7);
                    return taskDate >= weekAgo && taskDate <= today;
                case "month":
                    const monthAgo = new Date(today);
                    monthAgo.setMonth(today.getMonth() - 1);
                    return taskDate >= monthAgo && taskDate <= today;
                case "quarter":
                    const quarterAgo = new Date(today);
                    quarterAgo.setMonth(today.getMonth() - 3);
                    return taskDate >= quarterAgo && taskDate <= today;
                default:
                    return true;
            }
            }

            // If no filters are set, return all tasks
            return true;
        });
    };

    // Handle date range changes - clear time period when date range is set
    const handleDateRangeChange = (newDateRange) => {
        setDateRange(newDateRange);
        if (newDateRange.from || newDateRange.to) {
            setTimeFilter("week"); // Reset to default when date range is set
        }
    };

    // Handle time period changes - clear date range when time period is set
    const handleTimeFilterChange = (newTimeFilter) => {
        setTimeFilter(newTimeFilter);
        if (newTimeFilter !== "week") {
            setDateRange({ from: undefined, to: undefined }); // Clear date range when time period is set
        }
    };

    // If user doesn't have access to team view, force personal view
    useEffect(() => {
        if (!canAccessTeamView && viewMode === "team") {
            setViewMode("personal");
        }
    }, [canAccessTeamView, viewMode]);

    // Reset filters when switching between views
    useEffect(() => {
        // Only reset if viewMode actually changed (not on initial mount)
        if (previousViewModeRef.current !== viewMode && !isInitialMountRef.current) {
            const savedFilters = loadSavedFilters(viewMode);
            setTimeFilter(savedFilters.timeFilter);
            // Properly handle dateRange: if it's undefined/empty, use default, otherwise use saved value
            const savedDateRange = savedFilters.dateRange;
            if (savedDateRange && (savedDateRange.from || savedDateRange.to)) {
                setDateRange(savedDateRange);
            } else {
                setDateRange(getTodayRange());
            }
            setSelectedTeamMembers(savedFilters.selectedTeamMembers);
            setSelectedDepartment(savedFilters.selectedDepartment);
            setSelectedStatus(savedFilters.selectedStatus);
            setSelectedPriority(savedFilters.selectedPriority);
            setSelectedCategory(savedFilters.selectedCategory);
            setShowFilters(savedFilters.showFilters);
            previousViewModeRef.current = viewMode;
        }
        // Mark initial mount as complete after first render
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            previousViewModeRef.current = viewMode;
        }
    }, [viewMode]);

    // Save filters when they change (excluding viewMode which is handled by TTL hook)
    // Skip saving on initial mount or immediately after viewMode change
    useEffect(() => {
        // Skip on initial mount
        if (isInitialMountRef.current) {
            return;
        }
        
        // Only save if viewMode hasn't just changed (reset effect should run first)
        if (previousViewModeRef.current === viewMode) {
            saveFilters({
                timeFilter,
                dateRange,
                selectedTeamMembers,
                selectedDepartment,
                selectedStatus,
                selectedPriority,
                selectedCategory,
                showFilters,
            }, viewMode);
        }
    }, [viewMode, timeFilter, dateRange, selectedTeamMembers, selectedDepartment, selectedStatus, selectedPriority, selectedCategory, showFilters]);

    // Reset filters function
    const resetAllFilters = () => {
        // Clear saved filters from localStorage for the current view mode first
        const key = viewMode === 'team' ? 'dashboard-filters-team' : 'dashboard-filters-personal';
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.error('Error clearing saved filters:', error);
        }
        
        // Reset all filter states to default values
        setTimeFilter("week");
        setSelectedTeamMembers([]);
        setSelectedDepartment(null);
        setSelectedStatus("all");
        setSelectedPriority("all");
        setSelectedCategory("all");
        setDateRange(getTodayRange());
        setShowMobileFilters(false);
    };

    // Check if any filters are active
    const hasActiveFilters = selectedStatus !== "all" || 
                            selectedPriority !== "all" || 
                            selectedCategory !== "all" || 
                            (selectedDepartment && selectedDepartment !== "all") || 
                            selectedTeamMembers.length > 0 || 
                            dateRange.from;


    // Helper function to calculate department depth (0 for parent, 1 for child, etc.)
    const getDepartmentDepth = useCallback((dept, departments) => {
        if (!dept.parent_id) return 0;
        const parent = departments.find(d => d.id === dept.parent_id);
        if (!parent) return 0;
        return 1 + getDepartmentDepth(parent, departments);
    }, []);

    // Helper function to build hierarchical tree structure
    const buildDepartmentTree = useCallback((departments) => {
        if (!departments || departments.length === 0) return [];
        
        // Find all root departments (no parent)
        const rootDepartments = departments.filter(dept => !dept.parent_id);
        
        // Recursive function to get children of a department
        const getChildren = (parentId) => {
            return departments
                .filter(dept => dept.parent_id === parentId)
                .sort((a, b) => a.name.localeCompare(b.name));
        };
        
        // Recursive function to build tree structure
        const buildTree = (parentId) => {
            const children = getChildren(parentId);
            return children.map(child => ({
                ...child,
                children: buildTree(child.id)
            }));
        };
        
        // Build tree for each root department
        return rootDepartments
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(root => ({
                ...root,
                children: buildTree(root.id)
            }));
    }, []);

    // Helper function to flatten tree in hierarchical order (parent before children)
    const sortDepartmentsHierarchically = useCallback((departments) => {
        if (!departments || departments.length === 0) return [];
        
        const tree = buildDepartmentTree(departments);
        const result = [];
        
        // Recursive function to flatten tree
        const flattenTree = (nodes) => {
            nodes.forEach(node => {
                result.push(node);
                if (node.children && node.children.length > 0) {
                    flattenTree(node.children);
                }
            });
        };
        
        flattenTree(tree);
        return result;
    }, [buildDepartmentTree]);

    // Memoized sorted departments list for hierarchical display
    const sortedDepartments = useMemo(() => {
        if (!departments || departments.length === 0) return [];
        return sortDepartmentsHierarchically(departments);
    }, [departments, sortDepartmentsHierarchically]);

    // Helper function to get all child department IDs for a given parent department ID
    const getChildDepartmentIds = useCallback((parentId) => {
        if (!departments || departments.length === 0) {
            return [];
        }
        
        const childIds = [];
        const findChildren = (parentId) => {
            departments.forEach(dept => {
                if (dept.parent_id == parentId) {
                    childIds.push(dept.id);
                    findChildren(dept.id); // Recursively find grandchildren
                }
            });
        };
        
        findChildren(parentId);
        return childIds;
    }, [departments]);

    // Apply dashboard filters to tasks
    const applyDashboardFilters = useCallback((tasksToFilter) => {
        let filteredTasks = tasksToFilter;
        
        // Filter by selected team members
        if (selectedTeamMembers.length > 0) {
            filteredTasks = filteredTasks.filter(task => 
                task.users && task.users.some(user => selectedTeamMembers.includes(user.id))
            );
        }
        
        // Filter by selected department (for admin users)
        if (selectedDepartment && selectedDepartment !== "all" && currentUser?.roles?.includes('admin')) {
            const selectedDeptId = Number(selectedDepartment);
            // Check if the selected department has children
            const childIds = getChildDepartmentIds(selectedDeptId);
            
            if (childIds.length > 0) {
                // Parent department selected: include tasks from parent + all child departments
                const allowedDeptIds = [selectedDeptId, ...childIds.map(id => Number(id))];
            filteredTasks = filteredTasks.filter(task => 
                    task.users && task.users.some(user => allowedDeptIds.includes(Number(user.department_id)))
                );
            } else {
                // Child department or department with no children: filter by exact department
                filteredTasks = filteredTasks.filter(task => 
                    task.users && task.users.some(user => Number(user.department_id) == selectedDeptId)
                );
            }
        }
        
        // Filter by status
        if (selectedStatus && selectedStatus !== "all") {
            filteredTasks = filteredTasks.filter(task => task.status === selectedStatus);
        }
        
        // Filter by priority
        if (selectedPriority && selectedPriority !== "all") {
            filteredTasks = filteredTasks.filter(task => {
                const priority = parseInt(task.priority) || 0;
                switch (selectedPriority) {
                    case 'high':
                        return priority >= 8;
                    case 'medium':
                        return priority >= 5 && priority < 8;
                    case 'low':
                        return priority >= 1 && priority < 5;
                    default:
                        return true;
                }
            });
        }
        
        // Filter by category
        if (selectedCategory && selectedCategory !== "all") {
            filteredTasks = filteredTasks.filter(task => 
                task.category && task.category.toLowerCase() === selectedCategory.toLowerCase()
            );
        }
        
        return filteredTasks;
    }, [selectedTeamMembers, selectedDepartment, currentUser, getChildDepartmentIds, selectedStatus, selectedPriority, selectedCategory]);

    // Helper function to get dynamic title and description for Completed Tasks card
    const getCompletedTasksCardInfo = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        
        // Check if we have a date range selected
        if (dateRange?.from && dateRange?.to) {
            const fromDate = new Date(dateRange.from);
            const toDate = new Date(dateRange.to);
            fromDate.setHours(0, 0, 0, 0);
            toDate.setHours(0, 0, 0, 0);
            
            // Check if it's a single day selection
            if (fromDate.getTime() === toDate.getTime()) {
                if (fromDate.getTime() === today.getTime()) {
                    return {
                        title: "Completed Today",
                        description: "Tasks completed today"
                    };
                } else if (fromDate.getTime() === tomorrow.getTime()) {
                    return {
                        title: "Completed Tomorrow",
                        description: "Tasks completed tomorrow"
                    };
                } else if (fromDate.getTime() === yesterday.getTime()) {
                    return {
                        title: "Completed Yesterday",
                        description: "Tasks completed yesterday"
                    };
                } else {
                    return {
                        title: `Completed ${format(fromDate, "MMM dd")}`,
                        description: `Tasks completed on ${format(fromDate, "MMM dd")}`
                    };
                }
            } else {
                // Multi-day range - determine the range type
                const daysDiff = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
                const includesToday = fromDate.getTime() <= today.getTime() && toDate.getTime() >= today.getTime();
                
                // Define range types with their titles and descriptions
                const rangeTypes = [
                    { days: 6, title: "Completed This Week", desc: "This week", altTitle: "Completed Last Week", altDesc: "Last week" },
                    { days: 7, title: "Completed Last 7 Days", desc: "Last 7 days" },
                    { days: 29, title: "Completed Last 30 Days", desc: "Last 30 days" },
                    { days: 89, title: "Completed Last 90 Days", desc: "Last 90 days" },
                    { days: 365, title: "Completed Year to Date", desc: "Year to date", altTitle: "Completed Last Year", altDesc: "Last year" }
                ];
                
                // Check for exact day matches
                for (const range of rangeTypes) {
                    if (daysDiff === range.days) {
                        const isCurrent = range.days === 6 || range.days === 365 ? includesToday : false;
                        return {
                            title: isCurrent && range.altTitle ? range.altTitle : range.title,
                            description: `${isCurrent && range.altDesc ? range.altDesc : range.desc}`
                        };
                    }
                }
                
                // Check for month ranges (28-31 days)
                if (daysDiff >= 28 && daysDiff <= 31) {
                    const isThisMonth = includesToday;
                    return {
                        title: isThisMonth ? "Completed This Month" : "Completed Last Month",
                        description: `${isThisMonth ? "This month" : "Last month"}`
                    };
                }
                
                // Custom range
                return {
                    title: "Completed in Range",
                    description: `Tasks completed ${format(fromDate, "MMM dd")} - ${format(toDate, "MMM dd")}`
                };
            }
        }
        
        // Default case (no date range or time filter)
        return {
            title: "Completed Tasks",
            description: "All completed tasks"
        };
    };

    // Helper function to get dynamic title and description for Tasks Due Today card
    const getTasksDueTodayCardInfo = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        
        // Check if we have a date range selected
        if (dateRange?.from && dateRange?.to) {
            const fromDate = new Date(dateRange.from);
            const toDate = new Date(dateRange.to);
            fromDate.setHours(0, 0, 0, 0);
            toDate.setHours(0, 0, 0, 0);
            
            // Check if it's a single day selection
            if (fromDate.getTime() === toDate.getTime()) {
                if (fromDate.getTime() === today.getTime()) {
                    return {
                        title: "Tasks Due Today",
                        description: "Tasks due today + overdue tasks",
                        subtitle: "Includes overdue tasks"
                    };
                } else if (fromDate.getTime() === tomorrow.getTime()) {
                    return {
                        title: "Tasks Due Tomorrow",
                        description: "Tasks due tomorrow + overdue tasks",
                        subtitle: "Includes overdue tasks"
                    };
                } else if (fromDate.getTime() === yesterday.getTime()) {
                    return {
                        title: "Tasks Due Yesterday",
                        description: "Tasks due yesterday + overdue tasks",
                        subtitle: "Includes overdue tasks"
                    };
                } else {
                    return {
                        title: `Tasks Due ${format(fromDate, "MMM dd")}`,
                        description: `Tasks due ${format(fromDate, "MMM dd")} + overdue tasks`,
                        subtitle: "Includes overdue tasks"
                    };
                }
            } else {
                // Multi-day range - determine the range type
                const daysDiff = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
                const includesToday = fromDate.getTime() <= today.getTime() && toDate.getTime() >= today.getTime();
                
                // Define range types with their titles and descriptions
                const rangeTypes = [
                    { days: 6, title: "Tasks Due This Week", desc: "This week", altTitle: "Tasks Due Last Week", altDesc: "Last week" },
                    { days: 7, title: "Tasks Due Last 7 Days", desc: "Last 7 days" },
                    { days: 29, title: "Tasks Due Last 30 Days", desc: "Last 30 days" },
                    { days: 89, title: "Tasks Due Last 90 Days", desc: "Last 90 days" },
                    { days: 365, title: "Tasks Due Year to Date", desc: "Year to date", altTitle: "Tasks Due Last Year", altDesc: "Last year" }
                ];
                
                // Check for exact day matches
                for (const range of rangeTypes) {
                    if (daysDiff === range.days) {
                        const isCurrent = range.days === 6 || range.days === 365 ? includesToday : false;
                        return {
                            title: isCurrent && range.altTitle ? range.altTitle : range.title,
                            description: `${isCurrent && range.altDesc ? range.altDesc : range.desc} + overdue tasks`,
                            subtitle: "Includes overdue tasks"
                        };
                    }
                }
                
                // Check for month ranges (28-31 days)
                if (daysDiff >= 28 && daysDiff <= 31) {
                    const isThisMonth = includesToday;
                    return {
                        title: isThisMonth ? "Tasks Due This Month" : "Tasks Due Last Month",
                        description: `${isThisMonth ? "This month" : "Last month"} + overdue tasks`,
                        subtitle: "Includes overdue tasks"
                    };
                }
                
                // Custom range
                return {
                    title: "Tasks Due in Range",
                    description: `Tasks due ${format(fromDate, "MMM dd")} - ${format(toDate, "MMM dd")} + overdue tasks`,
                    subtitle: "Includes overdue tasks"
                };
            }
        }
        
        // Default case (no date range or time filter)
        return {
            title: "Tasks Due Today",
            description: "Tasks due today + overdue tasks",
            subtitle: "Includes overdue tasks"
        };
    };

    // Get tasks due today (includes all overdue tasks + tasks due today/tomorrow within date range)
    const tasksDueToday = (() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // First, get all overdue tasks (regardless of date range)
        const overdueTasks = safeTasks.filter((task) => {
            const taskDate = getTaskDueDate(task);
            if (!taskDate) return false;
        
        const taskDateNormalized = new Date(taskDate);
        taskDateNormalized.setHours(0, 0, 0, 0);
        
            const isPastDue = taskDateNormalized < today && task.status !== "Completed" && task.status !== "Missed";

            // Filter based on view mode and user permissions
            if (viewMode === "team" && canAccessTeamView) {
                return isPastDue;
            } else {
                // For personal view, check if the task belongs to the current user
                const belongsToUser = task.users && (
                    task.users.some(user => user.email === currentUser?.email) ||
                    task.users.some(user => user.id === currentUser?.id)
                );
                
                return isPastDue && belongsToUser;
            }
        });

        // Then, get tasks due today or tomorrow that fall within the date range
        const tasksDueTodayInRange = filterTasksByDate(applyDashboardFilters(safeTasks.filter((task) => {
            const taskDate = getTaskDueDate(task);
            if (!taskDate) return false;

            const taskDateNormalized = new Date(taskDate);
            taskDateNormalized.setHours(0, 0, 0, 0);
            
            // Check if task is due today or tomorrow
        const isToday = taskDateNormalized.getTime() === today.getTime();
            const tomorrow = new Date(today);
            tomorrow.setDate(today.getDate() + 1);
            const isTomorrow = taskDateNormalized.getTime() === tomorrow.getTime();
            const isTodayOrTomorrow = isToday || isTomorrow;

        // Filter based on view mode and user permissions
        if (viewMode === "team" && canAccessTeamView) {
                return isTodayOrTomorrow && task.status !== "Completed" && task.status !== "Missed";
        } else {
            // For personal view, check if the task belongs to the current user
            const belongsToUser = task.users && (
                task.users.some(user => user.email === currentUser?.email) ||
                task.users.some(user => user.id === currentUser?.id)
            );
            
                return isTodayOrTomorrow && belongsToUser && task.status !== "Completed" && task.status !== "Missed";
        }
    })));

        // Combine overdue tasks (no date range filter) + tasks due today/tomorrow (with date range filter)
        const combinedTasks = [...overdueTasks, ...tasksDueTodayInRange];
        
        // Remove duplicates (in case a task is both overdue and due today/tomorrow)
        const uniqueTasks = combinedTasks.filter((task, index, self) => 
            index === self.findIndex(t => t.id === task.id)
        );

        return applyDashboardFilters(uniqueTasks);
    })();

    // Get overdue tasks
    const overdueTasks = (() => {
        const overdueTasksFiltered = safeTasks.filter((task) => {
        const taskDate = getTaskDueDate(task);
        if (!taskDate) return false;

        // Normalize dates to start of day to avoid timezone issues
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const taskDateNormalized = new Date(taskDate);
        taskDateNormalized.setHours(0, 0, 0, 0);
        
        const isOverdue = taskDateNormalized < today && task.status !== "Completed" && task.status !== "Missed";

        // Filter based on view mode and user permissions
        if (viewMode === "team" && canAccessTeamView) {
            return isOverdue;
        } else {
            // For personal view, check if the task belongs to the current user
            const belongsToUser = task.users && (
                task.users.some(user => user.email === currentUser?.email) ||
                task.users.some(user => user.id === currentUser?.id)
            );
            
            return isOverdue && belongsToUser;
        }
        });

        // Don't apply date range filter to overdue tasks for both team and personal views
        // Overdue tasks should always be visible regardless of date range
        return applyDashboardFilters(overdueTasksFiltered);
    })();

    // Get completed tasks
    const completedTasks = (() => {
        const completedTasksFiltered = safeTasks.filter((task) => {
        const isCompleted = task.status === "Completed" && task.completed_date && task.status !== "Missed";

        // Filter based on view mode and user permissions
        if (viewMode === "team" && canAccessTeamView) {
            return isCompleted;
        } else {
            // For personal view, check if the task belongs to the current user
            // Check both by email and by user ID to be more robust
            const belongsToUser = task.users && (
                task.users.some(user => user.email === currentUser?.email) ||
                task.users.some(user => user.id === currentUser?.id)
            );
            
            return isCompleted && belongsToUser;
        }
        });

        // Filter by completed_date within the date range
        const dateFilteredTasks = completedTasksFiltered.filter(task => {
            if (!task.completed_date) return false;
            
            const completedDate = new Date(task.completed_date);
            
            // If date range is set, filter by completed_date (prioritize date range over time filter)
            if (dateRange.from || dateRange.to) {
                const fromDate = dateRange.from ? new Date(dateRange.from) : null;
                const toDate = dateRange.to ? new Date(dateRange.to) : null;
                
                // Normalize dates to start of day for accurate comparison
                if (fromDate) {
                    fromDate.setHours(0, 0, 0, 0);
                }
                if (toDate) {
                    toDate.setHours(23, 59, 59, 999); // End of day
                }
                completedDate.setHours(0, 0, 0, 0);
                
                if (fromDate && completedDate < fromDate) return false;
                if (toDate && completedDate > toDate) return false;
                
                return true;
            }
            
            // If time period is set (and no date range), use that for filtering
            if (timeFilter && timeFilter !== "week") {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                switch (timeFilter) {
                    case "day":
                        const taskDateNormalized = new Date(completedDate);
                        taskDateNormalized.setHours(0, 0, 0, 0);
                        const todayNormalized = new Date(today);
                        todayNormalized.setHours(0, 0, 0, 0);
                        return taskDateNormalized.getTime() === todayNormalized.getTime();
                    case "tomorrow":
                        const taskDateNormalizedTomorrow = new Date(completedDate);
                        taskDateNormalizedTomorrow.setHours(0, 0, 0, 0);
                        const tomorrow = new Date(today);
                        tomorrow.setDate(today.getDate() + 1);
                        tomorrow.setHours(0, 0, 0, 0);
                        return taskDateNormalizedTomorrow.getTime() === tomorrow.getTime();
                    case "week":
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        return completedDate >= weekAgo && completedDate <= today;
                    case "month":
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(today.getMonth() - 1);
                        return completedDate >= monthAgo && completedDate <= today;
                    case "quarter":
                        const quarterAgo = new Date(today);
                        quarterAgo.setMonth(today.getMonth() - 3);
                        return completedDate >= quarterAgo && completedDate <= today;
                    default:
                        return true;
                }
            }
            
            // If no filters are set, return all completed tasks
            return true;
        });

        // Apply additional dashboard filters
        return applyDashboardFilters(dateFilteredTasks);
    })();

    // Get upcoming tasks (due today or in next 48 hours)
    const upcomingTasks = filterTasksByDate(applyDashboardFilters(safeTasks.filter((task) => {
        const taskDate = getTaskDueDate(task);
        if (!taskDate) return false;

        const now = new Date();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Normalize task date to start of day for comparison
        const taskDateNormalized = new Date(taskDate);
        taskDateNormalized.setHours(0, 0, 0, 0);
        
        const isToday = taskDateNormalized.getTime() === today.getTime();
        const hoursUntilDue = (taskDate.getTime() - now.getTime()) / (1000 * 60 * 60);
        const isUpcoming = hoursUntilDue > 0 && hoursUntilDue <= 48;
        const isDueToday = isToday && task.status !== "Completed" && task.status !== "Missed";
        const isUpcomingOrDueToday = isDueToday || isUpcoming;

        // Filter based on view mode and user permissions
        if (viewMode === "team" && canAccessTeamView) {
            return isUpcomingOrDueToday && task.status !== "Missed";
        } else {
            // For personal view, check if the task belongs to the current user
            const belongsToUser = task.users && (
                task.users.some(user => user.email === currentUser?.email) ||
                task.users.some(user => user.id === currentUser?.id)
            );
            
            return isUpcomingOrDueToday && belongsToUser && task.status !== "Missed";
        }
    }))).sort(
        (a, b) => {
            const aDate = getTaskDueDate(a);
            const bDate = getTaskDueDate(b);
            if (!aDate || !bDate) return 0;
            return aDate.getTime() - bDate.getTime();
        }
    );

    // Filtered and sorted tasks for modals
    const filteredTasksDueToday = useMemo(() => {
        let filtered = tasksDueToday.filter(task => {
            // Search filter
            if (tasksDueTodaySearchTerm && !task.name.toLowerCase().includes(tasksDueTodaySearchTerm.toLowerCase())) {
                return false;
            }
            
            // Status filter
            if (tasksDueTodayStatusFilter !== "All" && task.status !== tasksDueTodayStatusFilter) {
                return false;
            }
            
            // Priority filter
            if (tasksDueTodayPriorityFilter !== "All" && task.priority !== Number(tasksDueTodayPriorityFilter)) {
                return false;
            }
            
            return true;
        });

        // Sort
        filtered.sort((a, b) => {
            let aValue, bValue;
            switch (tasksDueTodaySortField) {
                case "name":
                    aValue = a.name || "";
                    bValue = b.name || "";
                    break;
                case "priority":
                    aValue = a.priority || "";
                    bValue = b.priority || "";
                    break;
                case "status":
                    aValue = a.status || "";
                    bValue = b.status || "";
                    break;
                case "dueDate":
                    aValue = getTaskDueDate(a);
                    bValue = getTaskDueDate(b);
                    break;
                default:
                    aValue = a.id;
                    bValue = b.id;
            }
            
            // Handle date comparison
            if (tasksDueTodaySortField === "dueDate") {
                if (!aValue && !bValue) return 0;
                if (!aValue) return 1;
                if (!bValue) return -1;
                aValue = aValue.getTime();
                bValue = bValue.getTime();
            }
            
            if (aValue < bValue) return tasksDueTodaySortDirection === "asc" ? -1 : 1;
            if (aValue > bValue) return tasksDueTodaySortDirection === "asc" ? 1 : -1;
            return 0;
        });
        
        return filtered;
    }, [tasksDueToday, tasksDueTodaySearchTerm, tasksDueTodayStatusFilter, tasksDueTodayPriorityFilter, tasksDueTodaySortField, tasksDueTodaySortDirection]);

    const filteredOverdueTasks = useMemo(() => {
        let filtered = overdueTasks.filter(task => {
            // Search filter
            if (overdueTasksSearchTerm && !task.name.toLowerCase().includes(overdueTasksSearchTerm.toLowerCase())) {
                return false;
            }
            
            // Status filter
            if (overdueTasksStatusFilter !== "All" && task.status !== overdueTasksStatusFilter) {
                return false;
            }
            
            // Priority filter
            if (overdueTasksPriorityFilter !== "All" && task.priority !== Number(overdueTasksPriorityFilter)) {
                return false;
            }
            
            return true;
        });

        // Sort
        filtered.sort((a, b) => {
            let aValue, bValue;
            switch (overdueTasksSortField) {
                case "name":
                    aValue = a.name || "";
                    bValue = b.name || "";
                    break;
                case "priority":
                    aValue = a.priority || "";
                    bValue = b.priority || "";
                    break;
                case "status":
                    aValue = a.status || "";
                    bValue = b.status || "";
                    break;
                case "dueDate":
                    aValue = getTaskDueDate(a);
                    bValue = getTaskDueDate(b);
                    break;
                default:
                    aValue = a.id;
                    bValue = b.id;
            }
            
            // Handle date comparison
            if (overdueTasksSortField === "dueDate") {
                if (!aValue && !bValue) return 0;
                if (!aValue) return 1;
                if (!bValue) return -1;
                aValue = aValue.getTime();
                bValue = bValue.getTime();
            }
            
            if (aValue < bValue) return overdueTasksSortDirection === "asc" ? -1 : 1;
            if (aValue > bValue) return overdueTasksSortDirection === "asc" ? 1 : -1;
            return 0;
        });
        
        return filtered;
    }, [overdueTasks, overdueTasksSearchTerm, overdueTasksStatusFilter, overdueTasksPriorityFilter, overdueTasksSortField, overdueTasksSortDirection]);

    const filteredCompletedTasks = useMemo(() => {
        let filtered = completedTasks.filter(task => {
            // Search filter
            if (completedTasksSearchTerm && !task.name.toLowerCase().includes(completedTasksSearchTerm.toLowerCase())) {
                return false;
            }
            
            // Status filter
            if (completedTasksStatusFilter !== "All" && task.status !== completedTasksStatusFilter) {
                return false;
            }
            
            // Priority filter
            if (completedTasksPriorityFilter !== "All" && task.priority !== Number(completedTasksPriorityFilter)) {
                return false;
            }
            
            return true;
        });

        // Sort
        filtered.sort((a, b) => {
            let aValue, bValue;
            switch (completedTasksSortField) {
                case "name":
                    aValue = a.name || "";
                    bValue = b.name || "";
                    break;
                case "priority":
                    aValue = a.priority || "";
                    bValue = b.priority || "";
                    break;
                case "status":
                    aValue = a.status || "";
                    bValue = b.status || "";
                    break;
                case "completedDate":
                    aValue = a.completed_date ? new Date(a.completed_date) : null;
                    bValue = b.completed_date ? new Date(b.completed_date) : null;
                    break;
                default:
                    aValue = a.id;
                    bValue = b.id;
            }
            
            // Handle date comparison
            if (completedTasksSortField === "completedDate") {
                if (!aValue && !bValue) return 0;
                if (!aValue) return 1;
                if (!bValue) return -1;
                aValue = aValue.getTime();
                bValue = bValue.getTime();
            }
            
            if (aValue < bValue) return completedTasksSortDirection === "asc" ? -1 : 1;
            if (aValue > bValue) return completedTasksSortDirection === "asc" ? 1 : -1;
            return 0;
        });
        
        return filtered;
    }, [completedTasks, completedTasksSearchTerm, completedTasksStatusFilter, completedTasksPriorityFilter, completedTasksSortField, completedTasksSortDirection]);

    // Generate task activity data based on date range
    const generateTaskActivityData = () => {
        // Determine the date range to show
        let dateRangeToShow;
        let isSingleDay = false;
        
        if (dateRange.from || dateRange.to) {
            // Use custom date range
            const startDate = dateRange.from ? new Date(dateRange.from) : new Date();
            const endDate = dateRange.to ? new Date(dateRange.to) : new Date();
            
            // Check if it's a single day
            const startTime = startDate.getTime();
            const endTime = endDate.getTime();
            const dayInMs = 24 * 60 * 60 * 1000;
            isSingleDay = Math.abs(endTime - startTime) < dayInMs;
            
            dateRangeToShow = { start: startDate, end: endDate };
        } else {
            // Use time filter
            const today = new Date();
            const startDate = new Date(today);
            
            switch (timeFilter) {
                case "day":
                    startDate.setDate(today.getDate());
                    isSingleDay = true;
                    break;
                case "tomorrow":
                    startDate.setDate(today.getDate() + 1);
                    isSingleDay = true;
                    break;
                case "week":
                    startDate.setDate(today.getDate() - 6);
                    break;
                case "month":
                    startDate.setMonth(today.getMonth() - 1);
                    break;
                case "quarter":
                    startDate.setMonth(today.getMonth() - 3);
                    break;
                case "year":
                    startDate.setFullYear(today.getFullYear() - 1);
                    break;
                default:
                    startDate.setDate(today.getDate() - 6);
            }
            
            // For tomorrow, set end date to tomorrow as well
            const endDate = timeFilter === "tomorrow" ? new Date(today.getTime() + 24 * 60 * 60 * 1000) : today;
            dateRangeToShow = { start: startDate, end: endDate };
        }

        // If it's a single day, show 7 days centered around that day
        if (isSingleDay) {
            const centerDate = new Date(dateRangeToShow.start);
            const startDate = new Date(centerDate);
            startDate.setDate(centerDate.getDate() - 3);
            const endDate = new Date(centerDate);
            endDate.setDate(centerDate.getDate() + 3);
            dateRangeToShow = { start: startDate, end: endDate };
        }

        // Generate array of dates in the range
        const dates = [];
        const currentDate = new Date(dateRangeToShow.start);
        currentDate.setHours(0, 0, 0, 0);
        
        while (currentDate <= dateRangeToShow.end) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return dates.map((date) => {
            // Get tasks that were completed on this specific date
            const completedTasksOnDate = safeTasks.filter((task) => {
                if (!task.completed_date || task.status === "Missed") return false;
                
                // Normalize dates to start of day for comparison
                const completedDate = new Date(task.completed_date);
                completedDate.setHours(0, 0, 0, 0);
                const dateNormalized = new Date(date);
                dateNormalized.setHours(0, 0, 0, 0);
                const isSameDay = completedDate.getTime() === dateNormalized.getTime();
                
                if (!isSameDay) return false;

                // Apply view mode and user permissions filter
                if (viewMode === "team" && canAccessTeamView) {
                    return true;
                } else {
                    const belongsToUser = task.users && (
                        task.users.some(user => user.email === currentUser?.email) ||
                        task.users.some(user => user.id === currentUser?.id)
                    );
                    return belongsToUser;
                }
            });

            // Get tasks that were updated on this specific date (but not completed)
            const updatedTasksOnDate = safeTasks.filter((task) => {
                if (!task.updated_at || task.status === "Completed" || task.status === "Missed") return false;
                
                // Check if created_at and updated_at are the same (meaning it was created, not updated)
                if (task.created_at && task.updated_at) {
                    const createdDate = new Date(task.created_at);
                    const updatedDate = new Date(task.updated_at);
                    const isCreated = createdDate.getTime() === updatedDate.getTime();
                    if (isCreated) return false; // Exclude tasks that were only created, not updated
                }
                
                // Normalize dates to start of day for comparison
                const updatedDate = new Date(task.updated_at);
                updatedDate.setHours(0, 0, 0, 0);
                const dateNormalized = new Date(date);
                dateNormalized.setHours(0, 0, 0, 0);
                const isSameDay = updatedDate.getTime() === dateNormalized.getTime();
                
                if (!isSameDay) return false;

                // Apply view mode and user permissions filter
                if (viewMode === "team" && canAccessTeamView) {
                    return true;
                } else {
                    const belongsToUser = task.users && (
                        task.users.some(user => user.email === currentUser?.email) ||
                        task.users.some(user => user.id === currentUser?.id)
                    );
                    return belongsToUser;
                }
            });

            // Apply additional dashboard filters
            const filteredCompletedTasks = applyDashboardFilters(completedTasksOnDate);
            const filteredUpdatedTasks = applyDashboardFilters(updatedTasksOnDate);

            // If this is a single day filter, only show data for the originally selected day
            if (isSingleDay) {
                const originalSelectedDate = dateRange.from ? new Date(dateRange.from) : new Date();
                const currentDate = new Date(date);
                const originalDateNormalized = new Date(originalSelectedDate);
                originalDateNormalized.setHours(0, 0, 0, 0);
                const currentDateNormalized = new Date(currentDate);
                currentDateNormalized.setHours(0, 0, 0, 0);
                
                // Only show data if this is the originally selected day
                if (originalDateNormalized.getTime() !== currentDateNormalized.getTime()) {
                    return {
                        name: "Task Activity",
                        date: format(date, "MMM dd"),
                        fullDate: date.toISOString().split('T')[0],
                        total: 0,
                        completed: 0,
                        updated: 0,
                    };
                }
            }

            const completed = filteredCompletedTasks.length;
            const updated = filteredUpdatedTasks.length;
            const total = completed + updated;

            return {
                name: "Task Activity",
                date: format(date, "MMM dd"),
                fullDate: date.toISOString().split('T')[0],
                total: total,
                completed: completed,
                updated: updated,
            };
        });
    };

    // Generate task status data for the pie chart
    const generateTaskStatusData = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
        
        // Helper function to check if completed_date falls within date range
        const isCompletedDateInRange = (task) => {
            if (!task.completed_date) return false;
            if (!dateRange.from && !dateRange.to) return false; // No date range set
            
            const completedDate = new Date(task.completed_date);
            completedDate.setHours(0, 0, 0, 0);
            
            const fromDate = dateRange.from ? new Date(dateRange.from) : null;
            const toDate = dateRange.to ? new Date(dateRange.to) : null;
            
            if (fromDate) {
                fromDate.setHours(0, 0, 0, 0);
                if (completedDate < fromDate) return false;
            }
            if (toDate) {
                toDate.setHours(23, 59, 59, 999);
                if (completedDate > toDate) return false;
            }
            
            return true;
        };
        
        // Filter tasks based on view mode and user permissions
        let filteredTasks;
        if (viewMode === "team" && canAccessTeamView) {
            // Team view: filter by date range (due date) and include completed tasks with completed_date in range
            const tasksByDueDate = filterTasksByDate(applyDashboardFilters(safeTasks.filter(task => task.status !== "Missed")));
            
            // Get completed tasks with completed_date in the date range
            const completedTasksInRange = applyDashboardFilters(safeTasks.filter((task) => {
                return task.status === "Completed" && task.status !== "Missed" && isCompletedDateInRange(task);
            }));
            
            // Combine and remove duplicates
            filteredTasks = [...tasksByDueDate, ...completedTasksInRange].filter((task, index, self) => 
                index === self.findIndex(t => t.id === task.id)
            );
        } else {
            // Personal view: include overdue tasks and tasks due today regardless of date range
            const userTasks = applyDashboardFilters(safeTasks.filter(
                (task) => task.users && task.status !== "Missed" && (
                    task.users.some(user => user.email === currentUser?.email) ||
                    task.users.some(user => user.id === currentUser?.id)
                )
            ));

            // Get overdue tasks (regardless of date range) - include all statuses
            const overdueTasks = userTasks.filter((task) => {
                const taskDate = getTaskDueDate(task);
                if (!taskDate) return false;
                
                const taskDateNormalized = new Date(taskDate);
                taskDateNormalized.setHours(0, 0, 0, 0);
                
                return taskDateNormalized < today;
            });

            // Get tasks due today (regardless of date range) - include all statuses
            const tasksDueToday = userTasks.filter((task) => {
                const taskDate = getTaskDueDate(task);
                if (!taskDate) return false;
                
                const taskDateNormalized = new Date(taskDate);
                taskDateNormalized.setHours(0, 0, 0, 0);
                
                return taskDateNormalized.getTime() === today.getTime();
            });

            // Get other tasks that fall within the date range (by due date)
            const otherTasks = filterTasksByDate(userTasks);
            
            // Get completed tasks with completed_date in the date range
            const completedTasksInRange = userTasks.filter((task) => {
                return task.status === "Completed" && isCompletedDateInRange(task);
            });

            // Combine all tasks and remove duplicates
            const allTasks = [...overdueTasks, ...tasksDueToday, ...otherTasks, ...completedTasksInRange];
            filteredTasks = allTasks.filter((task, index, self) => 
                index === self.findIndex(t => t.id === task.id)
            );
        }

        // Count tasks by status
        const statusCounts = {
            completed: 0,
            inProgress: 0,
            toDo: 0,
            overdue: 0
        };

        filteredTasks.forEach(task => {
            if (task.status === "Completed") {
                statusCounts.completed++;
            } else if (task.status === "In Progress") {
                statusCounts.inProgress++;
            } else if (task.status === "To Do") {
                // Check if it's overdue by comparing due date with today
                const taskDate = getTaskDueDate(task);

                if (taskDate && !isNaN(taskDate.getTime())) {
                    taskDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
                    if (taskDate < today) {
                        statusCounts.overdue++;
                    } else {
                        statusCounts.toDo++;
                    }
                } else {
                    // No due date, count as "To Do"
                    statusCounts.toDo++;
                }
            }
        });

        const statusData = [
            {
                name: "Completed",
                value: statusCounts.completed,
                color: "#10b981",
            },
            {
                name: "In Progress",
                value: statusCounts.inProgress,
                color: "#3b82f6",
            },
            {
                name: "To Do",
                value: statusCounts.toDo,
                color: "#94a3b8",
            },
            {
                name: "Overdue",
                value: statusCounts.overdue,
                color: "#ef4444",
            },
        ];

        return statusData;
    };

    // Generate recent activity for the current user
    const generateRecentActivity = () => {
        const activities = [];
        
        // Get recent task completions
        const recentCompletedTasks = filterTasksByDate(applyDashboardFilters(safeTasks
            .filter(task => {
                if (task.status !== "Completed" || !task.completed_date) return false;
                
                // Filter based on view mode and user permissions
                if (viewMode === "team" && canAccessTeamView) {
                    return true;
                } else {
                    const belongsToUser = task.users && (
                        task.users.some(user => user.email === currentUser?.email) ||
                        task.users.some(user => user.id === currentUser?.id)
                    );
                    return belongsToUser;
                }
            }))
            .sort((a, b) => new Date(b.completed_date) - new Date(a.completed_date))
            .slice(0, 5));

        recentCompletedTasks.forEach(task => {
            activities.push({
                id: `completed-${task.id}`,
                type: 'task_completed',
                title: `Completed task: ${task.name}`,
                user: task.users?.[0]?.firstName + ' ' + task.users?.[0]?.lastName || 'Unknown',
                timestamp: task.completed_date,
                icon: CheckCircle2
            });
        });

        // Get recent task creations (using created_at if available, otherwise use id as proxy)
        const recentCreatedTasks = filterTasksByDate(applyDashboardFilters(safeTasks
            .filter(task => {
                // Filter based on view mode and user permissions
                if (viewMode === "team" && canAccessTeamView) {
                    return true;
                } else {
                    const belongsToUser = task.users && (
                        task.users.some(user => user.email === currentUser?.email) ||
                        task.users.some(user => user.id === currentUser?.id)
                    );
                    return belongsToUser;
                }
            }))
            .sort((a, b) => b.id - a.id) // Assuming higher ID means more recent
            .slice(0, 3));

        recentCreatedTasks.forEach(task => {
            // Check if task was created by a workflow (has workflow_instance_id or refid)
            const isWorkflowCreated = task.workflow_instance_id || task.refid;
            
            activities.push({
                id: `created-${task.id}`,
                type: 'task_created',
                title: `Created task: ${task.name}`,
                user: isWorkflowCreated ? 'via workflow' : (task.users?.[0]?.firstName + ' ' + task.users?.[0]?.lastName || 'Unknown'),
                timestamp: task.created_at || new Date().toISOString(),
                icon: Activity
            });
        });

        // Sort all activities by timestamp
        return activities
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, 6);
    };

    const taskActivityData = generateTaskActivityData();
    const taskStatusData = generateTaskStatusData();
    const recentActivity = generateRecentActivity();

    // Helper function to get parent department for manager
    const getManagerParentDepartment = useMemo(() => {
        if (!currentUser?.roles?.includes('manager') || currentUser?.roles?.includes('admin')) {
            return null;
        }
        if (!currentUser?.department_id || !departments || departments.length === 0) {
            return null;
        }
        
        // Find the manager's department
        const managerDept = departments.find(dept => dept.id == currentUser.department_id);
        if (!managerDept) {
            return null;
        }
        
        // If it has a parent_id, find the parent
        if (managerDept.parent_id) {
            const parentDept = departments.find(dept => dept.id == managerDept.parent_id);
            return parentDept || null;
        }
        
        // If no parent, this is the parent department
        return managerDept;
    }, [currentUser, departments]);

    // Helper function to check if a department is a child of the manager's parent department
    const isChildDepartment = useCallback((deptId) => {
        if (!getManagerParentDepartment) return false;
        const childIds = getChildDepartmentIds(getManagerParentDepartment.id);
        return childIds.includes(Number(deptId));
    }, [getManagerParentDepartment, getChildDepartmentIds]);

    // Update the team members section to use actual task data and role-based filtering
    const teamMembersData = useMemo(() => {
        let filteredUsers = users;
        
        // Filter users based on current user's role and permissions
        if (currentUser?.roles?.includes('admin')) {
            // Admin sees all users, but can filter by department
            if (selectedDepartment && selectedDepartment !== "all") {
                const selectedDeptId = Number(selectedDepartment);
                // Check if the selected department has children
                const childIds = getChildDepartmentIds(selectedDeptId);
                
                if (childIds.length > 0) {
                    // Parent department selected: show users from parent + all child departments
                    const allowedDeptIds = [selectedDeptId, ...childIds.map(id => Number(id))];
                filteredUsers = users.filter(user => {
                        const userDeptId = Number(user.department_id);
                        return allowedDeptIds.includes(userDeptId);
                    });
                } else {
                    // Child department or department with no children: show only users from that department
                    filteredUsers = users.filter(user => {
                        const userDeptId = Number(user.department_id);
                        return userDeptId == selectedDeptId;
                    });
                }
            } else {
                filteredUsers = users;
            }
        } else if (currentUser?.roles?.includes('manager') && currentUser?.department_id && !currentUser?.roles?.includes('admin')) {
            // Manager sees users based on department selection
            if (selectedDepartment && selectedDepartment !== "all") {
                const selectedDeptId = Number(selectedDepartment);
                const parentDept = getManagerParentDepartment;
                
                if (parentDept && selectedDeptId == parentDept.id) {
                    // Parent department selected: show users from parent + all child departments
                    const childIds = getChildDepartmentIds(parentDept.id);
                    const allowedDeptIds = [Number(parentDept.id), ...childIds.map(id => Number(id))];
                    filteredUsers = users.filter(user => {
                        const userDeptId = Number(user.department_id);
                        const isIncluded = allowedDeptIds.includes(userDeptId);
                        if (!isIncluded) {
                        }
                        return isIncluded;
                    });
                } else if (isChildDepartment(selectedDeptId)) {
                    // Child department selected: show only users from that child department
                    filteredUsers = users.filter(user => 
                        Number(user.department_id) == selectedDeptId
                    );
                } else {
                    // Fallback: show users from manager's department
            filteredUsers = users.filter(user => user.department_id == currentUser.department_id);
                }
            } else {
                // "All Departments" selected: show users from parent + all child departments
                const parentDept = getManagerParentDepartment;
                if (parentDept) {
                    const childIds = getChildDepartmentIds(parentDept.id);
                    const allowedDeptIds = [Number(parentDept.id), ...childIds.map(id => Number(id))];
                    filteredUsers = users.filter(user => {
                        const userDeptId = Number(user.department_id);
                        const isIncluded = allowedDeptIds.includes(userDeptId);
                        if (!isIncluded) {}
                        return isIncluded;
                    });
                } else {
                    // Fallback: show users from manager's department
                    filteredUsers = users.filter(user => user.department_id == currentUser.department_id);
                }
            }
        } else {
            // Regular users only see themselves
            filteredUsers = users.filter(user => user.id === currentUser?.id);
        }
        
        // Don't filter by selectedTeamMembers here - we want all members to show in the dropdown
        // The filtering for dashboard data happens in applyDashboardFilters
        
        return filteredUsers.map((user) => {
            // Get tasks for this user
            const userTasks = safeTasks.filter(task => 
                task.users && task.users.some(taskUser => taskUser.id === user.id)
            );
            
            // Helper function to check if completed_date falls within date range
            const isCompletedDateInRange = (task) => {
                if (!task.completed_date) return false;
                if (!dateRange.from && !dateRange.to) return false;
                
                const completedDate = new Date(task.completed_date);
                completedDate.setHours(0, 0, 0, 0);
                
                const fromDate = dateRange.from ? new Date(dateRange.from) : null;
                const toDate = dateRange.to ? new Date(dateRange.to) : null;
                
                if (fromDate) {
                    fromDate.setHours(0, 0, 0, 0);
                    if (completedDate < fromDate) return false;
                }
                if (toDate) {
                    toDate.setHours(23, 59, 59, 999);
                    if (completedDate > toDate) return false;
                }
                
                return true;
            };
            
            // Get tasks that are active/due today within the date range
                const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            // Get completed tasks within the date range
            const completedInRange = userTasks.filter(task => {
                return task.status === "Completed" && isCompletedDateInRange(task);
            });
            
            // Get active tasks (To Do or In Progress)
            // For the graph, we want to show all active tasks (matching the Active Tasks card)
            // The date range filter should only apply to completed tasks
            const activeTasks = userTasks.filter(task => 
                task.status === "To Do" || task.status === "In Progress"
            );
            
            // Total active tasks = active tasks + completed in range
            // This represents all tasks that should be tracked for this period
            const totalActive = activeTasks.length + completedInRange.length;
            
            const fullName = `${user.firstName} ${user.lastName}`;
            return {
                id: user.id,
                name: fullName,
                displayName: formatEmployeeNameForGraph(fullName),
                role: user.role,
                avatar: user.avatar,
                department_id: user.department_id,
                tasks: {
                    completed: completedInRange.length,
                    active: totalActive,
                    remaining: activeTasks.length,
                    total: userTasks.length,
                    overdue: userTasks.filter(task => {
                        const taskDate = getTaskDueDate(task);
                        if (!taskDate) return false;
                        const taskDateNormalized = new Date(taskDate);
                        taskDateNormalized.setHours(0, 0, 0, 0);
                        return taskDateNormalized < today && task.status !== "Completed";
                    }).length,
                },
            };
        });
    }, [users, safeTasks, currentUser, selectedDepartment, selectedTeamMembers, getManagerParentDepartment, getChildDepartmentIds, isChildDepartment, dateRange, filterTasksByDate, getTaskDueDate, formatEmployeeNameForGraph]);

    // Generate team quality data from actual task data
    const generateTeamQualityData = () => {
        return teamMembersData.map(member => {
            const userTasks = applyDashboardFilters(safeTasks).filter(task => 
                task.users && task.users.some(taskUser => taskUser.id === member.id)
            );
            
            const completedTasks = userTasks.filter(task => task.status === "Completed");
            const accuracy = userTasks.length > 0 ? Math.round((completedTasks.length / userTasks.length) * 100) : 0;
            
            return {
                name: member.name,
                accuracy: accuracy,
                qualityScore: Math.min(5, (accuracy / 20) + 3), // Simple quality score based on accuracy
            };
        });
    };

    // Generate team speed data from actual task data
    const generateTeamSpeedData = () => {
        return teamMembersData.map(member => {
            const userTasks = applyDashboardFilters(safeTasks).filter(task => 
                task.users && task.users.some(taskUser => taskUser.id === member.id)
            );
            
            const completedTasks = userTasks.filter(task => task.status === "Completed");
            const overdueTasks = userTasks.filter(task => {
                const today = new Date();
                const taskDate = new Date(task.dueDate);
                return taskDate < today && task.status !== "Completed";
            });
            
            const onTimeTasks = completedTasks.length - overdueTasks.length;
            const earlyTasks = Math.max(0, Math.floor(completedTasks.length * 0.2)); // Estimate early tasks
            const lateTasks = Math.max(0, overdueTasks.length);
            
            return {
                name: member.name,
                early: earlyTasks,
                onTime: Math.max(0, onTimeTasks),
                late: lateTasks,
            };
        });
    };

    const teamQualityData = useMemo(() => generateTeamQualityData(), [teamMembersData, safeTasks, selectedTeamMembers, selectedDepartment]);
    const teamSpeedData = useMemo(() => generateTeamSpeedData(), [teamMembersData, safeTasks, selectedTeamMembers, selectedDepartment]);

    // Memoize filtered tasks for dashboard cards
    const filteredTasksForCards = useMemo(() => applyDashboardFilters(safeTasks), [safeTasks, selectedTeamMembers, selectedDepartment]);

    // Calculate active tasks based on user role
    const activeTasks = useMemo(() => {
        let filteredTasks = applyDashboardFilters(safeTasks.filter(task => 
            task.status === "To Do" || task.status === "In Progress"
        ));
        
        if (currentUser?.roles?.includes('admin')) {
            // Admin sees all active tasks in the system, but can filter by department
            if (selectedDepartment && selectedDepartment !== "all") {
                return filteredTasks.filter(task => 
                    task.users && task.users.some(user => 
                        user.department_id == selectedDepartment
                    )
                );
            } else {
                return filteredTasks;
            }
        } else if (currentUser?.roles?.includes('manager') && currentUser?.department_id) {
            // Manager sees active tasks from users in their department
            return filteredTasks.filter(task => 
                task.users && task.users.some(user => 
                    user.department_id == currentUser.department_id
                )
            );
        } else {
            // Regular users only see their own active tasks
            return filteredTasks.filter(task => 
                task.users && task.users.some(user => user.id === currentUser?.id)
            );
        }
    }, [safeTasks, currentUser, selectedDepartment, selectedTeamMembers]);

    // Get active tasks label based on user role
    const getActiveTasksLabel = () => {
        if (currentUser?.roles?.includes('admin')) {
            return "Active Tasks in System";
        } else if (currentUser?.roles?.includes('manager')) {
            return "Active Tasks in Department";
        } else {
            return "Your Active Tasks";
        }
    };

    // Filter overdue tasks based on table filters
    const filteredOverdueTasksForTable = useMemo(() => {
        let filtered = overdueTasks.filter(task => {
            // Search filter
            if (overdueSearchTerm && !task.name.toLowerCase().includes(overdueSearchTerm.toLowerCase()) &&
                !task.id.toString().includes(overdueSearchTerm) &&
                !task.users?.some(user => user.name.toLowerCase().includes(overdueSearchTerm.toLowerCase()))) {
                return false;
            }
            
            // Priority filter
            if (overduePriorityFilter !== "All" && task.priority !== overduePriorityFilter) {
                return false;
            }
            
            // Status filter
            if (overdueStatusFilter !== "All" && task.status !== overdueStatusFilter) {
                return false;
            }
            
            // Date filter
            if (overdueDateFilter !== "All") {
                const taskDate = getTaskDueDate(task);
                if (!taskDate) return false;
                
                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                const weekAgo = new Date(today);
                weekAgo.setDate(today.getDate() - 7);
                const monthAgo = new Date(today);
                monthAgo.setMonth(today.getMonth() - 1);
                
                switch (overdueDateFilter) {
                    case "Today":
                        // Normalize dates to start of day for comparison
                        const taskDateNormalized = new Date(taskDate);
                        taskDateNormalized.setHours(0, 0, 0, 0);
                        const todayNormalized = new Date(today);
                        todayNormalized.setHours(0, 0, 0, 0);
                        return taskDateNormalized.getTime() === todayNormalized.getTime();
                    case "Yesterday":
                        // Normalize dates to start of day for comparison
                        const taskDateNormalizedYesterday = new Date(taskDate);
                        taskDateNormalizedYesterday.setHours(0, 0, 0, 0);
                        const yesterdayNormalized = new Date(yesterday);
                        yesterdayNormalized.setHours(0, 0, 0, 0);
                        return taskDateNormalizedYesterday.getTime() === yesterdayNormalized.getTime();
                    case "This Week":
                        return taskDate >= weekAgo && taskDate <= today;
                    case "This Month":
                        return taskDate >= monthAgo && taskDate <= today;
                    default:
                        return true;
                }
            }
            
            return true;
        });

        // Sort the filtered tasks
        filtered.sort((a, b) => {
            let aValue, bValue;
            
            switch (overdueSortField) {
                case "id":
                    aValue = a.id;
                    bValue = b.id;
                    break;
                case "priority":
                    aValue = a.priority || "";
                    bValue = b.priority || "";
                    break;
                case "status":
                    aValue = a.status || "";
                    bValue = b.status || "";
                    break;
                case "dueDate":
                    aValue = getTaskDueDate(a);
                    bValue = getTaskDueDate(b);
                    break;
                default:
                    aValue = a.id;
                    bValue = b.id;
            }
            
            // Handle date comparison
            if (overdueSortField === "dueDate") {
                if (!aValue && !bValue) return 0;
                if (!aValue) return 1;
                if (!bValue) return -1;
                aValue = aValue.getTime();
                bValue = bValue.getTime();
            }
            
            // Handle string comparison for priority and status
            if (typeof aValue === "string" && typeof bValue === "string") {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }
            
            if (overdueSortDirection === "asc") {
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else {
                return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            }
        });
        
        return filtered;
    }, [overdueTasks, overdueSearchTerm, overduePriorityFilter, overdueStatusFilter, overdueDateFilter, overdueSortField, overdueSortDirection]);

    // Paginate overdue tasks
    const paginatedOverdueTasks = filteredOverdueTasksForTable.slice(
        overduePage * overdueRowsPerPage,
        overduePage * overdueRowsPerPage + overdueRowsPerPage
    );

    // Handle overdue tasks page change
    const handleOverduePageChange = (newPage) => {
        setOverduePage(newPage);
    };

    // Filter active tasks for modal
    const filteredActiveTasks = useMemo(() => {
        let filtered = activeTasks.filter(task => {
            // Search filter
            if (activeTasksSearchTerm && !task.name.toLowerCase().includes(activeTasksSearchTerm.toLowerCase()) &&
                !task.id.toString().includes(activeTasksSearchTerm) &&
                !task.users?.some(user => user.name.toLowerCase().includes(activeTasksSearchTerm.toLowerCase()))) {
                return false;
            }
            
            // Status filter
            if (activeTasksStatusFilter !== "All" && task.status !== activeTasksStatusFilter) {
                return false;
            }
            // Priority filter
            if (activeTasksPriorityFilter !== "All" && task.priority !== Number(activeTasksPriorityFilter)) {   
                return false;
            }
            
            return true;
        });
        // Sort by due date (earliest first)
        filtered.sort((a, b) => {
            const aDate = getTaskDueDate(a);
            const bDate = getTaskDueDate(b);
            
            if (!aDate && !bDate) return 0;
            if (!aDate) return 1;
            if (!bDate) return -1;
            
            return aDate.getTime() - bDate.getTime();
        });
        
        return filtered;
    }, [activeTasks, activeTasksSearchTerm, activeTasksStatusFilter, activeTasksPriorityFilter]);

    // Paginate active tasks
    const paginatedActiveTasks = filteredActiveTasks.slice(
        activeTasksPage * activeTasksRowsPerPage,
        activeTasksPage * activeTasksRowsPerPage + activeTasksRowsPerPage
    );

    // Handle active tasks page change
    const handleActiveTasksPageChange = (newPage) => {
        setActiveTasksPage(newPage);
    };

    // Filter all tasks for modal (completion rate card)
    const filteredAllTasks = useMemo(() => {
        let filtered = applyDashboardFilters(safeTasks).filter(task => {
            // Search filter
            if (allTasksSearchTerm && !task.name.toLowerCase().includes(allTasksSearchTerm.toLowerCase()) &&
                !task.id.toString().includes(allTasksSearchTerm) &&
                !task.users?.some(user => user.name.toLowerCase().includes(allTasksSearchTerm.toLowerCase()))) {
                return false;
            }
            
            // Status filter
            if (allTasksStatusFilter !== "All" && task.status !== allTasksStatusFilter) {
                return false;
            }
            
            // Priority filter
            if (allTasksPriorityFilter !== "All" && task.priority !== Number(allTasksPriorityFilter)) {
                return false;
            }
            
            return true;
        });

        // Sort by due date (earliest first), then by status
        filtered.sort((a, b) => {
            const aDate = getTaskDueDate(a);
            const bDate = getTaskDueDate(b);
            
            // First sort by status priority (Completed last, Overdue first)
            const statusPriority = { "To Do": 1, "In Progress": 2, "Completed": 4 };
            const aPriority = statusPriority[a.status] || 3;
            const bPriority = statusPriority[b.status] || 3;
            
            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }
            
            // Then sort by due date
            if (!aDate && !bDate) return 0;
            if (!aDate) return 1;
            if (!bDate) return -1;
            
            return aDate.getTime() - bDate.getTime();
        });
        
        return filtered;
    }, [safeTasks, allTasksSearchTerm, allTasksStatusFilter, allTasksPriorityFilter, selectedTeamMembers, selectedDepartment]);

    // Paginate all tasks
    const paginatedAllTasks = filteredAllTasks.slice(
        allTasksPage * allTasksRowsPerPage,
        allTasksPage * allTasksRowsPerPage + allTasksRowsPerPage
    );

    // Handle all tasks page change
    const handleAllTasksPageChange = (newPage) => {
        setAllTasksPage(newPage);
    };

    // Reset overdue tasks pagination when filters change
    useEffect(() => {
        setOverduePage(0);
    }, [overdueSearchTerm, overduePriorityFilter, overdueStatusFilter, overdueDateFilter]);

    // Reset active tasks pagination when filters change
    useEffect(() => {
        setActiveTasksPage(0);
    }, [activeTasksSearchTerm, activeTasksStatusFilter, activeTasksPriorityFilter]);

    // Reset all tasks pagination when filters change
    useEffect(() => {
        setAllTasksPage(0);
    }, [allTasksSearchTerm, allTasksStatusFilter, allTasksPriorityFilter]);


    useEffect(() => {
        // Check if we should show the login success toast and refresh CSRF token
        const showLoginSuccess = localStorage.getItem("showLoginSuccess");

        if (showLoginSuccess) {
            // Show success toast
            toast({
                title: "Login successful",
                description: "Welcome to DME dashboard",
                variant: "success",
            });

            // Remove the flag so it doesn't show again on refresh
            localStorage.removeItem("showLoginSuccess");
            
            // Refresh the CSRF token by making a request to get a fresh token
            fetch('/token', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(response => {
                if (response.ok) {
                    return response.text();
                }
            }).then(token => {
                if (token) {
                    // Update the meta tag with the fresh token
                    const metaTag = csrf_token;
                    if (metaTag) {
                        metaTag.setAttribute('content', token);
                    }
                }
            }).catch(error => {
                console.error('Failed to refresh CSRF token:', error);
            });
        }
    }, []); // Empty dependency array since this should only run once on mount

    const toggleTeamMember = useCallback((id) => {
        setSelectedTeamMembers(prev => {
            if (prev.includes(id)) {
                return prev.filter((memberId) => memberId !== id);
            } else {
                return [...prev, id];
            }
        });
    }, []);

    // Helper function to safely format task dates
    const formatTaskDate = (task) => {
        let taskDate;
        if (task.defer_date) {
            taskDate = new Date(task.defer_date);
        } else if (task.dueDate) {
            taskDate = new Date(task.dueDate);
        } else if (task.due) {
            taskDate = new Date(task.due);
        }
        
        if (taskDate && !isNaN(taskDate.getTime())) {
            return format(taskDate, "MMM dd, yyyy");
        } else {
            return "No due date";
        }
    };

    // Helper function to get activity icon
    const getActivityIcon = (type) => {
        switch (type) {
            case "task_created":
                return Activity;
            case "task_completed":
                return CheckCircle2;
            case "workflow_created":
                return GitBranch;
            case "workflow_run":
                return Activity;
            default:
                return Activity;
        }
    };

    // Helper function to format time ago
    const getTimeAgo = (timestamp) => {
        const now = new Date();
        const activityTime = new Date(timestamp);
        const diffInHours = Math.floor(
            (now.getTime() - activityTime.getTime()) / (1000 * 60 * 60)
        );

        if (diffInHours < 1) return "Just now";
        if (diffInHours < 24) return `${diffInHours} hours ago`;
        if (diffInHours < 48) return "Yesterday";
        return format(activityTime, "MMM dd, yyyy");
    };

    // Helper function to refresh CSRF token
    const refreshCsrfToken = async () => {
        try {
            console.log('Refreshing CSRF token...');
            const tokenResponse = await fetch('/token', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            });
            
            if (tokenResponse.ok) {
                const newToken = await tokenResponse.text();
                console.log('New CSRF token received:', newToken.substring(0, 10) + '...');
                
                // Update the meta tag with the fresh token
                const metaTag = csrf_token;
                if (metaTag) {
                    metaTag.setAttribute('content', newToken);
                    console.log('CSRF token updated in meta tag');
                }
                
                return newToken;
            } else {
                throw new Error('Failed to refresh CSRF token');
            }
        } catch (error) {
            console.error('Error refreshing CSRF token:', error);
            throw error;
        }
    };

    // Helper function to handle CSRF token errors and retry
    const handleCsrfError = async (apiCall) => {
        try {
            return await apiCall();
        } catch (error) {
            if (error.message === '419') {
                // CSRF token mismatch - try to refresh the token and retry
                console.log('CSRF token expired, refreshing...');
                
                try {
                    const newToken = await refreshCsrfToken();
                    console.log('Retrying API call with fresh token...');
                    
                    // Retry the API call with the fresh token
                    return await apiCall(newToken);
                } catch (tokenError) {
                    console.error('Error refreshing CSRF token:', tokenError);
                    toast({
                        title: "Error",
                        description: "Session expired. Please refresh the page and try again.",
                        variant: "destructive",
                    });
                    throw tokenError;
                }
            } else {
                throw error;
            }
        }
    };

    // Helper function to get workflow status badge (matching workflows table)
    const getWorkflowStatusBadge = (status) => {
        switch (status) {
            case "active":
                return <Badge variant="outline" className="bg-green-100 text-green-800">Active</Badge>;
            case "inactive":
                return (
                    <Badge variant="outline" className="text-muted-foreground">
                        Inactive
                    </Badge>
                );
            case "draft":
                return <Badge variant="secondary">Draft</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    // Helper function to get trigger icon and display
    const getWorkflowTrigger = (workflow) => {
        const triggerType = workflow.nodes?.[0]?.data?.triggerType || "manual";
        return {
            type: triggerType,
            display: triggerType.charAt(0).toUpperCase() + triggerType.slice(1)
        };
    };

    // Handle sorting for overdue tasks table
    const handleOverdueSort = (field) => {
        if (overdueSortField === field) {
            setOverdueSortDirection(overdueSortDirection === "asc" ? "desc" : "asc");
        } else {
            setOverdueSortField(field);
            setOverdueSortDirection("asc");
        }
    };

    // Handle opening task detail modal
    const handleOpenTaskDetail = (task) => {
        setSelectedTaskForDetail(task);
        setShowTaskDetailModal(true);
    };

    // Helper function to check if current user is assigned to the task
    const isCurrentUserAssigned = (task) => {
        if (!currentUser || !task.users) return false;
        return task.users.some(user => user.id === currentUser.id);
    };

    // Helper function to complete a task directly using Inertia
    const completeTaskDirectly = (task) => {
        router.post('/tasks/complete', {
            task_id: task.id,
            completed_by: currentUser?.id,
        }, {
            preserveScroll: true,
            onSuccess: () => {
                toast({
                    title: "Task completed",
                    description: `Task "${task.name}" has been marked as completed.`,
                });
            },
            onError: (errors) => {
                console.error('Error completing task:', errors);
                const errorMessage = errors.task_completion || errors.message || "Failed to complete task. Please try again.";
                toast({
                    title: "Error",
                    description: errorMessage,
                    variant: "destructive",
                });
            },
        });
    };

    // Handle task completion
    const handleCompleteTask = (task) => {
        // Only allow completion if user is assigned to the task
        if (!isCurrentUserAssigned(task)) {
            toast({
                title: "Permission Denied",
                description: "Only assigned users can complete tasks.",
                variant: "destructive",
            });
            return;
        }

        // In personal view, complete directly without modal
        if (viewMode === "personal" && isCurrentUserAssigned(task)) {
            completeTaskDirectly(task);
            return;
        }

        // In team view or other cases, show confirmation modal
        setTaskToComplete(task);
        setShowCompleteTaskDialog(true);
    };

    const confirmCompleteTask = () => {
        if (!taskToComplete) return;

        // Use Inertia router for seamless update
        router.post('/tasks/complete', {
                        task_id: taskToComplete.id,
                        completed_by: currentUser?.id,
        }, {
            preserveScroll: true,
            onSuccess: () => {
            toast({
                title: "Task completed",
                description: `Task "${taskToComplete.name}" has been marked as completed.`,
            });

                // Send email notification to assigned users (only in team view)
                if (viewMode === "team" && taskToComplete.users && taskToComplete.users.length > 0) {
                const assignedUsers = taskToComplete.users.map(user => user.name).join(', ');
                toast({
                    title: "Notification sent",
                    description: `Email notification sent to: ${assignedUsers}`,
                });
            }
            },
            onError: (errors) => {
                console.error('Error completing task:', errors);
                const errorMessage = errors.task_completion || errors.message || "Failed to complete task. Please try again.";
            toast({
                title: "Error",
                    description: errorMessage,
                variant: "destructive",
            });
            },
            onFinish: () => {
            setShowCompleteTaskDialog(false);
            setTaskToComplete(null);
            },
        });
    };

    // Task detail modal handlers
    const handleTaskDetailComplete = (taskId) => {
        // Find the task in the overdue tasks
        const task = overdueTasks.find(t => t.id === taskId);
        if (!task) return;

        // In personal view, complete directly without modal
        if (viewMode === "personal" && isCurrentUserAssigned(task)) {
            completeTaskDirectly(task);
            setShowTaskDetailModal(false);
            return;
        }

        // In team view or other cases, show confirmation dialog
        setTaskToComplete(task);
        setShowCompleteTaskDialog(true);
        setShowTaskDetailModal(false);
    };

    const handleTaskDetailDefer = async (taskId, newDate) => {
        try {
            await handleCsrfError(async (token = null) => {
                const response = await fetch('/tasks/defer', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': token || csrf_token,
                    },
                    body: JSON.stringify({
                        task_id: taskId,
                        defer_date: newDate,
                    }),
                });

                if (!response.ok) {
                    if (response.status === 419) {
                        throw new Error('419');
                    }
                    throw new Error('Failed to defer task');
                }

                return response;
            });

            toast({
                title: "Task deferred",
                description: "Task has been deferred to the new date.",
            });
            window.location.reload();
        } catch (error) {
            console.error('Error deferring task:', error);
            toast({
                title: "Error",
                description: "Failed to defer task. Please try again.",
                variant: "destructive",
            });
        }
    };

    const handleTaskDetailAddNote = async (taskId, noteContent, mentions) => {
        try {
            await handleCsrfError(async (token = null) => {
                const response = await fetch('/notes', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': token || csrf_token,
                    },
                    body: JSON.stringify({
                        task_id: taskId,
                        note: noteContent,
                        mentions: mentions,
                    }),
                });

                if (!response.ok) {
                    if (response.status === 419) {
                        throw new Error('419');
                    }
                    throw new Error('Failed to add note');
                }

                return response;
            });

            toast({
                title: "Note added",
                description: "Note has been added to the task.",
            });
            window.location.reload();
        } catch (error) {
            console.error('Error adding note:', error);
            toast({
                title: "Error",
                description: "Failed to add note. Please try again.",
                variant: "destructive",
            });
        }
    };

    const handleTaskDetailStatusUpdate = (taskId, newStatus) => {
        // Find the current task to check if we're reverting from Completed
        const currentTask = safeTasks.find(t => t.id === taskId);
        const wasCompleted = currentTask?.status === "Completed";
        
        // Prepare update data
        const updateData = {
            id: taskId,
                        status: newStatus,
        };
        
        // If reverting from Completed, clear completed_date
        if (wasCompleted && newStatus !== "Completed") {
            updateData.completed_date = null;
        }
        
        // Use Inertia router for seamless update
        router.put('/tasks', updateData, {
            preserveScroll: true,
            onSuccess: () => {
            toast({
                title: "Status updated",
                description: `Task status updated to "${newStatus}".`,
            });
            },
            onError: (errors) => {
                console.error('Error updating status:', errors);
            toast({
                title: "Error",
                    description: errors.message || "Failed to update status. Please try again.",
                variant: "destructive",
            });
            },
        });
    };

    const handleTaskDetailViewParent = (parentId) => {
        // Find the parent task
        const parentTask = tasks.find(t => t.id === parentId);
        if (parentTask) {
            setSelectedTaskForDetail(parentTask);
        }
    };

    // Create stable callback refs for each team member
    const memberCallbacks = useMemo(() => {
        const callbacks = {};
        teamMembersData.forEach(member => {
            callbacks[member.id] = () => toggleTeamMember(member.id);
        });
        return callbacks;
    }, [teamMembersData, toggleTeamMember]);

    // Filter activities to only show current user's activities
    // const userActivities = activities.filter(
    //     (activity) => activity.user === currentUser?.email
    // );

    // Get active workflows that the user has access to
    const activeWorkflows = workflows.filter((workflow) => {
        // Only show active workflows
        if (workflow.status !== "active") {
            return false;
        }

        // If no assignment type is set, show to all users (backward compatibility)
        if (!workflow.assignment_type) {
            return true;
        }

        const userId = currentUser?.id;
        const userDepartmentId = currentUser?.department_id;
        const userRoles = currentUser?.roles || [];

        switch (workflow.assignment_type) {
            case 'individual':
                // User must be in the assigned_user_ids array
                return workflow.assigned_user_ids && 
                       workflow.assigned_user_ids.includes(userId);
            
            case 'department':
                // User must be in one of the assigned departments
                return workflow.assigned_department_ids && 
                       workflow.assigned_department_ids.includes(userDepartmentId);
            
            case 'department_manager':
                // User must be in one of the assigned departments AND have manager role
                return workflow.assigned_department_ids && 
                       workflow.assigned_department_ids.includes(userDepartmentId) &&
                       userRoles.some(role => role.name === 'manager' || role.name === 'admin');
            
            default:
                return false;
        }
    });

    // Add getStatusColor and getPriorityColor helper functions for consistent badge styling
    const getStatusColor = (status) => {
      switch (status) {
        case "Completed":
          return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
        case "In Progress":
          return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
        case "To Do":
          return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
        default:
          return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
      }
    };

    const getPriorityColor = (priority) => {
      const priorityNum = Number.parseInt(priority, 10);
      if (priorityNum >= 8) {
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      } else if (priorityNum >= 5) {
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
      } else {
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      }
    };

    return (
        <>
            <Head title="DME | Dashboard" />
            <AuthenticatedLayout>
            <TaskProvider initialTasks={safeTasks}>
                <div className="flex flex-col gap-6 pb-8 pt-6 px-4 md:px-6 w-full">
                {/* Header Section */}
                <div className="flex flex-col gap-4">
                    <div className="flex flex-row gap-2 items-start relative">
                    <div className="flex flex-col gap-2">
                        <h1 className="text-3xl font-bold tracking-tight">
                            {viewMode === "team" && 
                             currentUser?.roles?.includes('manager') && 
                             !currentUser?.roles?.includes('admin') && 
                             getManagerParentDepartment
                                ? `${getManagerParentDepartment.name} Dashboard` 
                                : "Dashboard"}
                        </h1>
                        <p className="text-muted-foreground">
                            {viewMode === "personal"
                                ? "Overview of your workflow management system"
                                : "Team performance and workload overview"}
                        </p>
                    </div>

                        {/* Team View Toggle */}
                    {canAccessTeamView && (
                            <div className="flex items-center space-x-2 mt-2 sm:mt-0 absolute right-0 top-0">
                                <Label
                                    htmlFor="view-mode"
                                    className="text-sm font-medium"
                                >
                                    Team View
                                </Label>
                                <Switch
                                    id="view-mode"
                                    checked={viewMode === "team"}
                                    onCheckedChange={(checked) =>
                                        setViewMode(
                                            checked ? "team" : "personal"
                                        )
                                    }
                                />
                        </div>
                    )}
                    
                    {/* Reset All Button */}
                    {hasActiveFilters && (
                        <button
                            onClick={resetAllFilters}
                            className="hidden lg:block absolute right-0 bottom-0 text-blue-600 hover:text-blue-800 cursor-pointer text-sm font-medium transition-colors"
                        >
                            Reset All
                        </button>
                    )}
                    </div>

                    {/* Controls Section */}
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-end">
                        {/* Desktop Filters - Hidden on mobile */}
                        <div className="hidden lg:block w-full">
                            {isMobile ? (
                                <div className="space-y-6">
                                    <div className="space-y-6">
                                        {/* Date Range Filter */}
                                        <div className="flex flex-col gap-2">
                                            <Label className="text-sm font-medium">
                                                Date Range
                                            </Label>
                                            <DatePickerWithRange
                                                className="w-full"
                                                dateRange={dateRange}
                                                onDateRangeChange={handleDateRangeChange}
                                            />
                                        </div>

                                        {/* Department Filter */}
                                        {(currentUser?.roles?.includes('admin') || currentUser?.roles?.includes('manager')) && departments && departments.length > 0 && viewMode === "team" && (
                                            <div className="flex flex-col gap-2">
                                                <Label className="text-sm font-medium">
                                                    Department
                                                </Label>
                                                <Select
                                                    value={selectedDepartment || "all"}
                                                    onValueChange={(value) => setSelectedDepartment(value === "all" ? null : value)}
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder="All Departments" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">All Departments</SelectItem>
                                                        {sortedDepartments.map((dept, index) => {
                                                            const depth = getDepartmentDepth(dept, departments);
                                                            const nextDept = sortedDepartments[index + 1];
                                                            const nextDepth = nextDept ? getDepartmentDepth(nextDept, departments) : -1;
                                                            // A department is the last child if the next department is at the same or higher level (same or less depth)
                                                            const isLast = !nextDept || nextDepth <= depth;
                                                            
                                                            const prefix = depth === 0 ? '' : (isLast ? '└─ ' : '├─ ');
                                                            const indentClass = depth === 0 ? '' : depth === 1 ? 'pl-6' : depth === 2 ? 'pl-10' : 'pl-14';
                                                            
                                                            return (
                                                                <SelectItem key={dept.id} value={dept.id.toString()} className={indentClass}>
                                                                    <span className="flex items-center">
                                                                        {prefix && <span className="text-muted-foreground mr-1">{prefix}</span>}
                                                                {dept.name}
                                                                    </span>
                                                            </SelectItem>
                                                            );
                                                        })}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}

                                        {/* Status Filter */}
                                        <div className="flex flex-col gap-2">
                                            <Label className="text-sm font-medium">
                                                Status
                                            </Label>
                                            <Select
                                                value={selectedStatus}
                                                onValueChange={setSelectedStatus}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="All Statuses" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">All Statuses</SelectItem>
                                                    <SelectItem value="To Do">To Do</SelectItem>
                                                    <SelectItem value="In Progress">In Progress</SelectItem>
                                                    <SelectItem value="Completed">Completed</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Priority Filter */}
                                        <div className="flex flex-col gap-2">
                                            <Label className="text-sm font-medium">
                                                Priority
                                            </Label>
                                            <Select
                                                value={selectedPriority}
                                                onValueChange={setSelectedPriority}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="All Priorities" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">All Priorities</SelectItem>
                                                    <SelectItem value="high">High (8-10)</SelectItem>
                                                    <SelectItem value="medium">Medium (5-7)</SelectItem>
                                                    <SelectItem value="low">Low (1-4)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Category Filter */}
                                        {categories && categories.length > 0 && (
                                            <div className="flex flex-col gap-2">
                                                <Label className="text-sm font-medium">
                                                    Category
                                                </Label>
                                                <Select
                                                    value={selectedCategory}
                                                    onValueChange={setSelectedCategory}
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder="All Categories" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">All Categories</SelectItem>
                                                        {categories.map((category) => (
                                                            <SelectItem key={category} value={category}>
                                                                {category}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}

                                        {/* Team Members Filter */}
                                        {viewMode === "team" && (
                                            <div className="flex flex-col gap-2">
                                                <Label className="text-sm font-medium">
                                                    Team Members
                                                </Label>
                                                <Popover>
                                                    <PopoverTrigger asChild>
                                                        <Button variant="outline" className="w-full h-9 justify-between">
                                                            <span className="truncate font-normal">
                                                                {selectedTeamMembers.length === 0
                                                                    ? "All Members"
                                                                    : `${selectedTeamMembers.length} selected`}
                                                            </span>
                                                            <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-[220px] p-0" align="start">
                                                        <Command className="font-normal">
                                                            <CommandGroup>
                                                                <CommandItem
                                                                    onSelect={() => {
                                                                        setSelectedTeamMembers([]);
                                                                    }}
                                                                    className="justify-between"
                                                                >
                                                                    <span>All Members</span>
                                                                    {selectedTeamMembers.length === 0 && <Check className="h-4 w-4" />}
                                                                </CommandItem>
                                                                {teamMembersData.map((member) => {
                                                                    const isSelected = selectedTeamMembers.includes(member.id);
                                                                    return (
                                                                        <CommandItem
                                                                            key={member.id}
                                                                            onSelect={() => {
                                                                                const newValue = selectedTeamMembers.includes(member.id)
                                                                                    ? selectedTeamMembers.filter(id => id !== member.id)
                                                                                    : [...selectedTeamMembers, member.id];
                                                                                setSelectedTeamMembers(newValue);
                                                                            }}
                                                                            className={`justify-between ${isSelected ? "bg-gray-400/20 hover:bg-gray-400/30" : ""}`}
                                                                        >
                                                                            <span className="truncate font-normal">{member.name}</span>
                                                                        </CommandItem>
                                                                    );
                                                                })}
                                                            </CommandGroup>
                                                        </Command>
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                        {/* Date Range Filter */}
                                        <div className="flex flex-col gap-2">
                                            <Label className="text-sm font-medium">
                                                Date Range
                                            </Label>
                                            <DatePickerWithRange
                                                className="w-full"
                                                dateRange={dateRange}
                                                onDateRangeChange={handleDateRangeChange}
                                            />
                                        </div>

                                        {/* Department Filter */}
                                        {(currentUser?.roles?.includes('admin') || currentUser?.roles?.includes('manager')) && departments && departments.length > 0 && viewMode === "team" && (
                                            <div className="flex flex-col gap-2">
                                                <Label className="text-sm font-medium">
                                                    Department
                                                </Label>
                                                <Select
                                                    value={selectedDepartment || "all"}
                                                    onValueChange={(value) => setSelectedDepartment(value === "all" ? null : value)}
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder="All Departments" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">All Departments</SelectItem>
                                                        {sortedDepartments.map((dept, index) => {
                                                            const depth = getDepartmentDepth(dept, departments);
                                                            const nextDept = sortedDepartments[index + 1];
                                                            const nextDepth = nextDept ? getDepartmentDepth(nextDept, departments) : -1;
                                                            // A department is the last child if the next department is at the same or higher level (same or less depth)
                                                            const isLast = !nextDept || nextDepth <= depth;
                                                            
                                                            const prefix = depth === 0 ? '' : (isLast ? '└─ ' : '├─ ');
                                                            const indentClass = depth === 0 ? '' : depth === 1 ? 'pl-6' : depth === 2 ? 'pl-10' : 'pl-14';
                                                            
                                                            return (
                                                                <SelectItem key={dept.id} value={dept.id.toString()} className={indentClass}>
                                                                    <span className="flex items-center">
                                                                        {prefix && <span className="text-muted-foreground mr-1">{prefix}</span>}
                                                                {dept.name}
                                                                    </span>
                                                            </SelectItem>
                                                            );
                                                        })}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}

                                        {/* Status Filter */}
                                        <div className="flex flex-col gap-2">
                                            <Label className="text-sm font-medium">
                                                Status
                                            </Label>
                                            <Select
                                                value={selectedStatus}
                                                onValueChange={setSelectedStatus}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="All Statuses" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">All Statuses</SelectItem>
                                                    <SelectItem value="To Do">To Do</SelectItem>
                                                    <SelectItem value="In Progress">In Progress</SelectItem>
                                                    <SelectItem value="Completed">Completed</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Priority Filter */}
                                        <div className="flex flex-col gap-2">
                                            <Label className="text-sm font-medium">
                                                Priority
                                            </Label>
                                            <Select
                                                value={selectedPriority}
                                                onValueChange={setSelectedPriority}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="All Priorities" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">All Priorities</SelectItem>
                                                    <SelectItem value="high">High (8-10)</SelectItem>
                                                    <SelectItem value="medium">Medium (5-7)</SelectItem>
                                                    <SelectItem value="low">Low (1-4)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Category Filter */}
                                        {categories && categories.length > 0 && (
                                            <div className="flex flex-col gap-2">
                                                <Label className="text-sm font-medium">
                                                    Category
                                                </Label>
                                                <Select
                                                    value={selectedCategory}
                                                    onValueChange={setSelectedCategory}
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder="All Categories" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="all">All Categories</SelectItem>
                                                        {categories.map((category) => (
                                                            <SelectItem key={category} value={category}>
                                                                {category}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}

                                        {/* Team Members Filter */}
                                        {viewMode === "team" && (
                                            <div className="flex flex-col gap-2">
                                                <Label className="text-sm font-medium">
                                                    Team Members
                                                </Label>
                                                <Popover>
                                                    <PopoverTrigger asChild>
                                                        <Button variant="outline" className="w-full h-9 justify-between">
                                                            <span className="truncate font-normal">
                                                                {selectedTeamMembers.length === 0
                                                                    ? "All Members"
                                                                    : `${selectedTeamMembers.length} selected`}
                                                            </span>
                                                            <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-[220px] p-0" align="start">
                                                        <Command className="font-normal">
                                                            <CommandGroup>
                                                                <CommandItem
                                                                    onSelect={() => {
                                                                        setSelectedTeamMembers([]);
                                                                    }}
                                                                    className="justify-between"
                                                                >
                                                                    <span>All Members</span>
                                                                    {selectedTeamMembers.length === 0 && <Check className="h-4 w-4" />}
                                                                </CommandItem>
                                                                {teamMembersData.map((member) => {
                                                                    const isSelected = selectedTeamMembers.includes(member.id);
                                                                    return (
                                                                        <CommandItem
                                                                            key={member.id}
                                                                            onSelect={() => {
                                                                                const newValue = selectedTeamMembers.includes(member.id)
                                                                                    ? selectedTeamMembers.filter(id => id !== member.id)
                                                                                    : [...selectedTeamMembers, member.id];
                                                                                setSelectedTeamMembers(newValue);
                                                                            }}
                                                                            className={`justify-between ${isSelected ? "bg-gray-400/20 hover:bg-gray-400/30" : ""}`}
                                                                        >
                                                                            <span className="truncate font-normal">{member.name}</span>
                                                                        </CommandItem>
                                                                    );
                                                                })}
                                                            </CommandGroup>
                                                        </Command>
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Mobile Filters Button - Only visible on mobile */}
                        <div className="lg:hidden">
                            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
                                <SheetTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                        className="flex items-center gap-2 w-full"
                                >
                                    <Filter className="h-4 w-4" />
                                        <span>Filters</span>
                                        {hasActiveFilters && (
                                        <Badge
                                            variant="secondary"
                                            className="ml-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center"
                                        >
                                                {[
                                                    selectedStatus !== "all" ? 1 : 0,
                                                    selectedPriority !== "all" ? 1 : 0,
                                                    selectedCategory !== "all" ? 1 : 0,
                                                    (selectedDepartment && selectedDepartment !== "all") ? 1 : 0,
                                                    selectedTeamMembers.length > 0 ? 1 : 0,
                                                    dateRange.from ? 1 : 0
                                                ].reduce((a, b) => a + b, 0)}
                                        </Badge>
                                    )}
                                </Button>
                                </SheetTrigger>
                                <SheetContent 
                                    side="bottom" 
                                    className="h-[85vh] rounded-t-xl border-t-2"
                                >
                                    <div className="flex flex-col h-full">
                                        <SheetHeader className="pb-4 border-b">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <SheetTitle className="text-left">Dashboard Filters</SheetTitle>
                                                    <SheetDescription className="text-left">
                                                        Filter your dashboard data by various criteria
                                                    </SheetDescription>
                                                </div>
                                                {hasActiveFilters && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={resetAllFilters}
                                                        className="flex items-center gap-1"
                                                    >
                                                        <Filter className="h-4 w-4" />
                                                        Reset
                                                    </Button>
                                                )}
                                            </div>
                                        </SheetHeader>
                                        
                                        <div className="flex-1 overflow-y-auto py-6 px-1">
                                            {isMobile ? (
                                                <div className="space-y-6">
                                                    <div className="space-y-6">
                                                        {/* Date Range Filter */}
                                                        <div className="flex flex-col gap-2">
                                                            <Label className="text-sm font-medium">
                                                                Date Range
                                                            </Label>
                                                            <DatePickerWithRange
                                                                className="w-full"
                                                                dateRange={dateRange}
                                                                onDateRangeChange={handleDateRangeChange}
                                                            />
                                                        </div>

                                                        {/* Department Filter */}
                                                        {(currentUser?.roles?.includes('admin') || currentUser?.roles?.includes('manager')) && departments && departments.length > 0 && viewMode === "team" && (
                                                            <div className="flex flex-col gap-2">
                                                                <Label className="text-sm font-medium">
                                                                    Department
                                                                </Label>
                                                                <Select
                                                                    value={selectedDepartment || "all"}
                                                                    onValueChange={(value) => setSelectedDepartment(value === "all" ? null : value)}
                                                                >
                                                                    <SelectTrigger className="w-full">
                                                                        <SelectValue placeholder="All Departments" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="all">All Departments</SelectItem>
                                                                        {sortedDepartments.map((dept, index) => {
                                                                            const depth = getDepartmentDepth(dept, departments);
                                                                            const nextDept = sortedDepartments[index + 1];
                                                                            const nextDepth = nextDept ? getDepartmentDepth(nextDept, departments) : -1;
                                                                            // A department is the last child if the next department is at the same or higher level (same or less depth)
                                                                            const isLast = !nextDept || nextDepth <= depth;
                                                                            
                                                                            const prefix = depth === 0 ? '' : (isLast ? '└─ ' : '├─ ');
                                                                            const indentClass = depth === 0 ? '' : depth === 1 ? 'pl-6' : depth === 2 ? 'pl-10' : 'pl-14';
                                                                            
                                                                            return (
                                                                                <SelectItem key={dept.id} value={dept.id.toString()} className={indentClass}>
                                                                                    <span className="flex items-center">
                                                                                        {prefix && <span className="text-muted-foreground mr-1">{prefix}</span>}
                                                                                {dept.name}
                                                                                    </span>
                                                                            </SelectItem>
                                                                            );
                                                                        })}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        )}

                                                        {/* Status Filter */}
                                                        <div className="flex flex-col gap-2">
                                                            <Label className="text-sm font-medium">
                                                                Status
                                                            </Label>
                                                            <Select
                                                                value={selectedStatus}
                                                                onValueChange={setSelectedStatus}
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="All Statuses" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="all">All Statuses</SelectItem>
                                                                    <SelectItem value="To Do">To Do</SelectItem>
                                                                    <SelectItem value="In Progress">In Progress</SelectItem>
                                                                    <SelectItem value="Completed">Completed</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>

                                                        {/* Priority Filter */}
                                                        <div className="flex flex-col gap-2">
                                                            <Label className="text-sm font-medium">
                                                                Priority
                                                            </Label>
                                                            <Select
                                                                value={selectedPriority}
                                                                onValueChange={setSelectedPriority}
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="All Priorities" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="all">All Priorities</SelectItem>
                                                                    <SelectItem value="high">High (8-10)</SelectItem>
                                                                    <SelectItem value="medium">Medium (5-7)</SelectItem>
                                                                    <SelectItem value="low">Low (1-4)</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>

                                                        {/* Category Filter */}
                                                        {categories && categories.length > 0 && (
                                                            <div className="flex flex-col gap-2">
                                                                <Label className="text-sm font-medium">
                                                                    Category
                                                                </Label>
                                                                <Select
                                                                    value={selectedCategory}
                                                                    onValueChange={setSelectedCategory}
                                                                >
                                                                    <SelectTrigger className="w-full">
                                                                        <SelectValue placeholder="All Categories" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="all">All Categories</SelectItem>
                                                                        {categories.map((category) => (
                                                                            <SelectItem key={category} value={category}>
                                                                                {category}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        )}

                                                        {/* Team Members Filter */}
                                                        {viewMode === "team" && (
                                                            <div className="flex flex-col gap-2">
                                                                <Label className="text-sm font-medium">
                                                                    Team Members
                                                                </Label>
                                                                <Popover>
                                                                    <PopoverTrigger asChild>
                                                                        <Button variant="outline" className="w-full h-9 justify-between">
                                                                            <span className="truncate font-normal">
                                                                                {selectedTeamMembers.length === 0
                                                                                    ? "All Members"
                                                                                    : `${selectedTeamMembers.length} selected`}
                                                                            </span>
                                                                            <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                                                                        </Button>
                                                                    </PopoverTrigger>
                                                                    <PopoverContent className="w-[220px] p-0" align="start">
                                                                        <Command className="font-normal">
                                                                            <CommandGroup>
                                                                                <CommandItem
                                                                                    onSelect={() => {
                                                                                        setSelectedTeamMembers([]);
                                                                                    }}
                                                                                    className="justify-between"
                                                                                >
                                                                                    <span>All Members</span>
                                                                                    {selectedTeamMembers.length === 0 && <Check className="h-4 w-4" />}
                                                                                </CommandItem>
                                                                                {teamMembersData.map((member) => {
                                                                                    const isSelected = selectedTeamMembers.includes(member.id);
                                                                                    return (
                                                                                        <CommandItem
                                                                                            key={member.id}
                                                                                            onSelect={() => {
                                                                                                const newValue = selectedTeamMembers.includes(member.id)
                                                                                                    ? selectedTeamMembers.filter(id => id !== member.id)
                                                                                                    : [...selectedTeamMembers, member.id];
                                                                                                setSelectedTeamMembers(newValue);
                                                                                            }}
                                                                                            className={`justify-between ${isSelected ? "bg-gray-400/20 hover:bg-gray-400/30" : ""}`}
                                                                                        >
                                                                                            <span className="truncate font-normal">{member.name}</span>
                                                                                        </CommandItem>
                                                                                    );
                                                                                })}
                                                                            </CommandGroup>
                                                                        </Command>
                                                                    </PopoverContent>
                                                                </Popover>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="w-full">
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                                        {/* Date Range Filter */}
                                                        <div className="flex flex-col gap-2">
                                                            <Label className="text-sm font-medium">
                                                                Date Range
                                                            </Label>
                                                            <DatePickerWithRange
                                                                className="w-full"
                                                                dateRange={dateRange}
                                                                onDateRangeChange={handleDateRangeChange}
                                                            />
                                                        </div>

                                                        {/* Department Filter */}
                                                        {(currentUser?.roles?.includes('admin') || currentUser?.roles?.includes('manager')) && departments && departments.length > 0 && viewMode === "team" && (
                                                            <div className="flex flex-col gap-2">
                                                                <Label className="text-sm font-medium">
                                                                    Department
                                                                </Label>
                                                                <Select
                                                                    value={selectedDepartment || "all"}
                                                                    onValueChange={(value) => setSelectedDepartment(value === "all" ? null : value)}
                                                                >
                                                                    <SelectTrigger className="w-full">
                                                                        <SelectValue placeholder="All Departments" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="all">All Departments</SelectItem>
                                                                        {sortedDepartments.map((dept, index) => {
                                                                            const depth = getDepartmentDepth(dept, departments);
                                                                            const nextDept = sortedDepartments[index + 1];
                                                                            const nextDepth = nextDept ? getDepartmentDepth(nextDept, departments) : -1;
                                                                            // A department is the last child if the next department is at the same or higher level (same or less depth)
                                                                            const isLast = !nextDept || nextDepth <= depth;
                                                                            
                                                                            const prefix = depth === 0 ? '' : (isLast ? '└─ ' : '├─ ');
                                                                            const indentClass = depth === 0 ? '' : depth === 1 ? 'pl-6' : depth === 2 ? 'pl-10' : 'pl-14';
                                                                            
                                                                            return (
                                                                                <SelectItem key={dept.id} value={dept.id.toString()} className={indentClass}>
                                                                                    <span className="flex items-center">
                                                                                        {prefix && <span className="text-muted-foreground mr-1">{prefix}</span>}
                                                                                {dept.name}
                                                                                    </span>
                                                                            </SelectItem>
                                                                            );
                                                                        })}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        )}

                                                        {/* Status Filter */}
                                                        <div className="flex flex-col gap-2">
                                                            <Label className="text-sm font-medium">
                                                                Status
                                                            </Label>
                                                            <Select
                                                                value={selectedStatus}
                                                                onValueChange={setSelectedStatus}
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="All Statuses" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="all">All Statuses</SelectItem>
                                                                    <SelectItem value="To Do">To Do</SelectItem>
                                                                    <SelectItem value="In Progress">In Progress</SelectItem>
                                                                    <SelectItem value="Completed">Completed</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>

                                                        {/* Priority Filter */}
                                                        <div className="flex flex-col gap-2">
                                                            <Label className="text-sm font-medium">
                                                                Priority
                                                            </Label>
                                                            <Select
                                                                value={selectedPriority}
                                                                onValueChange={setSelectedPriority}
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="All Priorities" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="all">All Priorities</SelectItem>
                                                                    <SelectItem value="high">High (8-10)</SelectItem>
                                                                    <SelectItem value="medium">Medium (5-7)</SelectItem>
                                                                    <SelectItem value="low">Low (1-4)</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>

                                                        {/* Category Filter */}
                                                        {categories && categories.length > 0 && (
                                                            <div className="flex flex-col gap-2">
                                                                <Label className="text-sm font-medium">
                                                                    Category
                                                                </Label>
                                                                <Select
                                                                    value={selectedCategory}
                                                                    onValueChange={setSelectedCategory}
                                                                >
                                                                    <SelectTrigger className="w-full">
                                                                        <SelectValue placeholder="All Categories" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="all">All Categories</SelectItem>
                                                                        {categories.map((category) => (
                                                                            <SelectItem key={category} value={category}>
                                                                                {category}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        )}

                                                        {/* Team Members Filter */}
                                                        {viewMode === "team" && (
                                                            <div className="flex flex-col gap-2">
                                                                <Label className="text-sm font-medium">
                                                                    Team Members
                                                                </Label>
                                                                <Popover>
                                                                    <PopoverTrigger asChild>
                                                                        <Button variant="outline" className="w-full h-9 justify-between">
                                                                            <span className="truncate font-normal">
                                                                                {selectedTeamMembers.length === 0
                                                                                    ? "All Members"
                                                                                    : `${selectedTeamMembers.length} selected`}
                                                                            </span>
                                                                            <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                                                                        </Button>
                                                                    </PopoverTrigger>
                                                                    <PopoverContent className="w-[220px] p-0" align="start">
                                                                        <Command className="font-normal">
                                                                            <CommandGroup>
                                                                                <CommandItem
                                                                                    onSelect={() => {
                                                                                        setSelectedTeamMembers([]);
                                                                                    }}
                                                                                    className="justify-between"
                                                                                >
                                                                                    <span>All Members</span>
                                                                                    {selectedTeamMembers.length === 0 && <Check className="h-4 w-4" />}
                                                                                </CommandItem>
                                                                                {teamMembersData.map((member) => {
                                                                                    const isSelected = selectedTeamMembers.includes(member.id);
                                                                                    return (
                                                                                        <CommandItem
                                                                            key={member.id}
                                                                            onSelect={() => {
                                                                                const newValue = selectedTeamMembers.includes(member.id)
                                                                                    ? selectedTeamMembers.filter(id => id !== member.id)
                                                                                    : [...selectedTeamMembers, member.id];
                                                                                setSelectedTeamMembers(newValue);
                                                                            }}
                                                                            className={`justify-between ${isSelected ? "bg-gray-400/20 hover:bg-gray-400/30" : ""}`}
                                                                        >
                                                                            <span className="truncate font-normal">{member.name}</span>
                                                                        </CommandItem>
                                                                                    );
                                                                                })}
                                                                            </CommandGroup>
                                                                        </Command>
                                                                    </PopoverContent>
                                                                </Popover>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </SheetContent>
                            </Sheet>
                        </div>
                    </div>
                </div>

                {viewMode === "personal" ? (
                    <>
                        {/* Summary Cards */}
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            <Card 
                                className="bg-gradient-to-br from-yellow-50 via-orange-50 to-yellow-100 dark:from-yellow-900 dark:via-orange-900 dark:to-yellow-950 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => setShowTasksDueToday(true)}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        {getTasksDueTodayCardInfo().title}
                                    </CardTitle>
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">
                                        {tasksDueToday.length}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {tasksDueToday.length > 0
                                            ? getTasksDueTodayCardInfo().description
                                            : "No tasks due"}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card 
                                className="bg-gradient-to-br from-red-50 via-orange-50 to-red-100 dark:from-red-900 dark:via-orange-900 dark:to-red-950 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => setShowOverdueTasks(true)}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        Overdue Tasks
                                    </CardTitle>
                                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">
                                        {overdueTasks.length}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {overdueTasks.length > 0
                                            ? `${overdueTasks.length} tasks overdue`
                                            : "No overdue tasks"}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card 
                                className="bg-gradient-to-br from-green-50 via-green-100 to-green-200 dark:from-green-900 dark:via-green-800 dark:to-green-950 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => setShowCompletedTasks(true)}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        {getCompletedTasksCardInfo().title}
                                    </CardTitle>
                                    <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">
                                        {completedTasks.length}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {completedTasks.length > 0
                                            ? getCompletedTasksCardInfo().description
                                            : "No completed tasks"}
                                    </p>
                                </CardContent>
                            </Card>
                            {/* <Card
                                className="cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => setShowActiveWorkflows(true)}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        Active Workflows
                                    </CardTitle>
                                    <GitBranch className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">
                                        {activeWorkflows.length}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {activeWorkflows.length > 0
                                            ? `${activeWorkflows.length} active workflows`
                                            : "No active workflows"}
                                    </p>
                                </CardContent>
                            </Card> */}
                        </div>

                        <Tabs defaultValue="overview" className="space-y-4">
                            <TabsContent value="overview" className="space-y-4">
                                <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                                    <Card className="col-span-1">
                                        <CardHeader>
                                            <CardTitle>Task Status</CardTitle>
                                            <CardDescription>
                                                Current distribution of tasks by status
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="flex justify-center">
                                            {taskStatusData.length === 0 || taskStatusData.every(item => item.value === 0) ? (
                                                <div className="flex flex-col items-center justify-center h-[300px] text-center p-8">
                                                    <div className="w-16 h-16 bg-muted/20 rounded-full flex items-center justify-center mb-4">
                                                        <PieChartIcon className="w-8 h-8 text-muted-foreground" />
                                                    </div>
                                                    <h3 className="text-lg font-semibold text-foreground mb-2">No Status Data</h3>
                                                    <p className="text-sm text-muted-foreground max-w-sm">
                                                        No task status data found for the selected date range. Try adjusting your filters or date selection.
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="relative w-full h-[300px]">
                                                    <ResponsiveContainer
                                                        width="100%"
                                                        height="100%"
                                                    >
                                                        <PieChart>
                                                            <Pie
                                                                data={taskStatusData}
                                                                cx="50%"
                                                                cy="50%"
                                                                innerRadius={60}
                                                                outerRadius={80}
                                                                paddingAngle={5}
                                                                dataKey="value"
                                                            >
                                                                {taskStatusData.map(
                                                                    (entry, index) => (
                                                                        <Cell
                                                                            key={`cell-${index}`}
                                                                            fill={
                                                                                entry.color
                                                                            }
                                                                        />
                                                                    )
                                                                )}
                                                            </Pie>
                                                            <Tooltip 
                                                                wrapperStyle={{
                                                                    zIndex: 1000
                                                                }}
                                                                contentStyle={{
                                                                    backgroundColor: 'hsl(var(--background))',
                                                                    border: '1px solid hsl(var(--border))',
                                                                    borderRadius: '6px',
                                                                    color: 'hsl(var(--foreground))',
                                                                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
                                                                }}
                                                                labelStyle={{
                                                                    color: 'hsl(var(--muted-foreground))'
                                                                }}
                                                                itemStyle={{
                                                                    color: 'hsl(var(--foreground))'
                                                                }}
                                                            />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                    {/* Center label showing total tasks */}
                                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                        <div className="text-center">
                                                            <div className="text-2xl font-bold text-foreground">
                                                                {taskStatusData.reduce((sum, entry) => sum + entry.value, 0)}
                                                            </div>
                                                            <div className="text-xs text-muted-foreground">
                                                                Total Tasks
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </CardContent>
                                        {taskStatusData.length > 0 && !taskStatusData.every(item => item.value === 0) && (
                                            <div className="flex flex-wrap justify-center gap-2 sm:gap-4 pb-6">
                                                {taskStatusData.map(
                                                    (entry, index) => (
                                                        <div
                                                            key={index}
                                                            className="flex items-center gap-2"
                                                        >
                                                            <div
                                                                className="h-3 w-3 rounded-full"
                                                                style={{
                                                                    backgroundColor:
                                                                        entry.color,
                                                                }}
                                                            ></div>
                                                            <span className="text-sm">
                                                                {entry.name}
                                                            </span>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </Card>
                                    <Card className="col-span-1">
                                        <CardHeader>
                                            <CardTitle>Task Activity</CardTitle>
                                            <CardDescription>
                                                Tasks completed and updated on each day for the selected date range
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            {taskActivityData.length === 0 || taskActivityData.every(item => item.total === 0) ? (
                                                <div className="flex flex-col items-center justify-center h-[350px] text-center p-8">
                                                    <div className="w-16 h-16 bg-muted/20 rounded-full flex items-center justify-center mb-4">
                                                        <BarChart3 className="w-8 h-8 text-muted-foreground" />
                                                    </div>
                                                    <h3 className="text-lg font-semibold text-foreground mb-2">No Activity Data</h3>
                                                    <p className="text-sm text-muted-foreground max-w-sm">
                                                        No task activity found for the selected date range. Try adjusting your filters or date selection.
                                                    </p>
                                                </div>
                                            ) : (
                                                <ResponsiveContainer
                                                    width="100%"
                                                    height={350}
                                                >
                                                    <BarChart
                                                        data={taskActivityData}
                                                        margin={{
                                                            top: 5,
                                                            right: 5,
                                                            left: 0,
                                                            bottom: 5,
                                                        }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="date" />
                                                        <YAxis 
                                                            width={30} 
                                                            domain={[0, 'dataMax']}
                                                            tickCount={6}
                                                            allowDecimals={false}
                                                        />
                                                        <Tooltip 
                                                            formatter={(value, name) => [value, name]}
                                                            contentStyle={{
                                                                backgroundColor: 'hsl(var(--background))',
                                                                border: '1px solid hsl(var(--border))',
                                                                borderRadius: '6px',
                                                                color: 'hsl(var(--foreground))',
                                                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
                                                            }}
                                                            labelStyle={{
                                                                color: 'hsl(var(--muted-foreground))'
                                                            }}
                                                        />
                                                        <Bar
                                                            dataKey="updated"
                                                            stackId="tasks"
                                                            fill="#3b82f6"
                                                            name="Updated"
                                                        />
                                                        <Bar
                                                            dataKey="completed"
                                                            stackId="tasks"
                                                            fill="#10b981"
                                                            name="Completed"
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            )}
                                        </CardContent>
                                    </Card>
                                </div>
                                {/* <div className="grid gap-4 md:grid-cols-2">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>
                                                Recent Activity
                                            </CardTitle>
                                            <CardDescription>
                                                Latest task completions and creations
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-4">
                                                {recentActivity.length > 0 ? (
                                                    <>
                                                        {(showRecentActivityExpanded ? recentActivity : recentActivity.slice(0, 5)).map((activity) => {
                                                            const Icon = activity.icon;
                                                            return (
                                                                <div
                                                                    key={activity.id}
                                                                    className="flex items-start gap-4"
                                                                >
                                                                    <div className="rounded-full bg-muted p-2">
                                                                        <Icon className="h-4 w-4" />
                                                                    </div>
                                                                    <div className="flex-1 space-y-1">
                                                                        <p className="text-sm font-medium leading-none">
                                                                            {activity.title}
                                                                        </p>
                                                                        <p className="text-xs text-muted-foreground">
                                                                            {activity.user} • {getTimeAgo(activity.timestamp)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        {recentActivity.length > 5 && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => setShowRecentActivityExpanded(!showRecentActivityExpanded)}
                                                                className="w-full"
                                                            >
                                                                {showRecentActivityExpanded ? "Show Less" : `Show ${recentActivity.length - 5} More`}
                                                            </Button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground text-center py-4">
                                                        No recent activity
                                                    </p>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>
                                                Upcoming Tasks
                                            </CardTitle>
                                            <CardDescription>
                                                Tasks due today or in the next 48 hours
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <div className={`space-y-4 ${showUpcomingTasksExpanded ? 'max-h-80 overflow-y-auto' : ''}`}>
                                                {upcomingTasks.length > 0 ? (
                                                    <>
                                                        {(showUpcomingTasksExpanded ? upcomingTasks : upcomingTasks.slice(0, 5)).map(
                                                            (task) => (
                                                                <div
                                                                    key={task.id}
                                                                    className="flex items-center justify-between"
                                                                >
                                                                    <div className="space-y-1">
                                                                        <p className="text-sm font-medium leading-none">
                                                                            {task.name}
                                                                        </p>
                                                                        <p className="text-xs text-muted-foreground">
                                                                            {formatTaskDate(task)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            )
                                                        )}
                                                        {upcomingTasks.length > 5 && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => setShowUpcomingTasksExpanded(!showUpcomingTasksExpanded)}
                                                                className="w-full"
                                                            >
                                                                {showUpcomingTasksExpanded ? "Show Less" : `Show ${upcomingTasks.length - 5} More`}
                                                            </Button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground text-center py-4">
                                                        No upcoming tasks
                                                    </p>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div> */}
                            </TabsContent>
                        </Tabs>
                    </>
                ) : (
                    // Team Dashboard View
                    <>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <Card className="bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 dark:from-blue-900 dark:via-blue-800 dark:to-blue-950">
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        Team Members
                                    </CardTitle>
                                    <Users className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">
                                        {teamMembersData.length}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {currentUser?.roles?.includes('admin') 
                                            ? 'Total users in system' 
                                            : currentUser?.roles?.includes('manager') 
                                                ? 'Users in department' 
                                                : 'Your team members'}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card 
                                className="bg-gradient-to-br from-yellow-50 via-orange-50 to-yellow-100 dark:from-yellow-900 dark:via-orange-900 dark:to-yellow-950 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => setShowAllTasksModal(true)}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        Team Completion Rate
                                    </CardTitle>
                                    <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">
                                        {filteredTasksForCards.length > 0 ? Math.round((completedTasks.length / filteredTasksForCards.length) * 100) : 0}%
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {completedTasks.length} of {filteredTasksForCards.length} tasks completed
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="bg-gradient-to-br from-red-50 via-orange-50 to-red-100 dark:from-red-900 dark:via-orange-900 dark:to-red-950">
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        Overdue Tasks
                                    </CardTitle>
                                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">{overdueTasks.length}</div>
                                    <p className="text-xs text-muted-foreground">
                                        Across all team members
                                    </p>
                                </CardContent>
                            </Card>
                            <Card 
                                className="bg-gradient-to-br from-green-50 via-green-100 to-green-200 dark:from-green-900 dark:via-green-800 dark:to-green-950 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => setShowActiveTasksModal(true)}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        Active Tasks
                                    </CardTitle>
                                    <Activity className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">{activeTasks.length}</div>
                                    <p className="text-xs text-muted-foreground">
                                        {getActiveTasksLabel()}
                                    </p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Overdue Tasks Table for Team View */}
                        <Card>
                            {overdueTasks.length > 0 ? (
                                <>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                                                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                                                </div>
                                                <div>
                                                    <CardTitle className="text-lg font-semibold text-red-800 dark:text-red-200">
                                                        Overdue Tasks ({filteredOverdueTasksForTable.length})
                                                    </CardTitle>
                                                    <CardDescription className="text-red-600 dark:text-red-400">
                                                        ⚠️ Tasks that are past their due date and need immediate attention
                                                    </CardDescription>
                                                </div>
                                            </div>
                                            <Badge variant="destructive" className="text-sm font-medium flex items-center justify-center text-center min-w-[80px]">
                                                {filteredOverdueTasksForTable.length} Overdue
                                            </Badge>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {/* Filters */}
                                        <div className="mb-4 space-y-4 mt-4">
                                            <div className="flex flex-col sm:flex-row gap-4">
                                                {/* Search */}
                                                <div className="flex-1">
                                                    <div className="relative">
                                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                                                        <Input
                                                            placeholder="Search tasks, IDs, or assignees..."
                                                            value={overdueSearchTerm}
                                                            onChange={(e) => setOverdueSearchTerm(e.target.value)}
                                                            className="pl-10"
                                                        />
                                                    </div>
                                                </div>
                                                
                                                {/* Clear Filters Button */}
                                                {overdueSearchTerm && (
                                                    <div className="flex justify-end">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => {
                                                                setOverdueSearchTerm("");
                                                            }}
                                                        >
                                                            Clear Search
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="rounded-md border overflow-y-auto max-h-[500px]">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead className="w-[90px]">ID</TableHead>
                                                        <TableHead>Task</TableHead>
                                                        <TableHead>Assignee</TableHead>
                                                        {!isTablet && (
                                                            <TableHead className="w-[100px]">Priority</TableHead>
                                                        )}
                                                        <TableHead className="w-[100px]">Status</TableHead>
                                                        <TableHead className="text-right w-[120px]">Due Date</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {paginatedOverdueTasks.length > 0 ? (
                                                        paginatedOverdueTasks.map((task) => (
                                                            <TableRow 
                                                                key={task.id}
                                                                className="cursor-pointer bg-red-50 dark:bg-red-900/50 border-l-4 border-l-red-500 hover:bg-red-100 dark:hover:bg-red-900/35"
                                                                onClick={() => handleOpenTaskDetail(task)}
                                                            >
                                                                <TableCell className="font-medium text-xs">{task.id}</TableCell>
                                                                <TableCell className="font-medium">{task.name}</TableCell>
                                                                <TableCell>{task.users?.map(user => user.name).join(', ') || 'Unassigned'}</TableCell>
                                                                {!isTablet && (
                                                                    <TableCell>
                                                                        <Badge
                                                                            variant="outline"
                                                                            className={`ml-2 ${getPriorityColor(task.priority)}`}
                                                                        >
                                                                            {task.priority}
                                                                        </Badge>
                                                                    </TableCell>
                                                                )}
                                                                <TableCell>
                                                                    <Badge
                                                                        variant="outline"
                                                                        className={getStatusColor(task.status)}
                                                                    >
                                                                        {task.status}
                                                                    </Badge>
                                                                </TableCell>
                                                                <TableCell className="text-red-600 dark:text-red-400 font-medium">{formatTaskDate(task)}</TableCell>
                                                            </TableRow>
                                                        ))
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                                                No overdue tasks match the current filters
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>
                                        
                                        {/* Pagination for overdue tasks */}
                                        {filteredOverdueTasksForTable.length > 0 && (
                                            <div className="flex items-center justify-between p-4 mt-4 bg-card rounded-md border">
                                                <div className="flex items-center space-x-2">
                                                    <Select
                                                        value={overdueRowsPerPage.toString()}
                                                        onValueChange={(value) => {
                                                            setOverdueRowsPerPage(Number(value));
                                                            setOverduePage(0);
                                                        }}
                                                    >
                                                        <SelectTrigger className="w-[100px] h-8">
                                                            <SelectValue placeholder="Rows" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="10">10 rows</SelectItem>
                                                            <SelectItem value="20">20 rows</SelectItem>
                                                            <SelectItem value="50">50 rows</SelectItem>
                                                            <SelectItem value="100">100 rows</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <div className="text-sm text-muted-foreground">
                                                        Showing <strong>{paginatedOverdueTasks.length}</strong> of{" "}
                                                        <strong>{filteredOverdueTasksForTable.length}</strong> overdue tasks
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        onClick={() => handleOverduePageChange(overduePage - 1)}
                                                        disabled={overduePage === 0}
                                                    >
                                                        <ChevronLeft className="h-4 w-4" />
                                                    </Button>
                                                    {Array.from({
                                                        length: Math.ceil(filteredOverdueTasksForTable.length / overdueRowsPerPage),
                                                    })
                                                        .map((_, index) => (
                                                            <Button
                                                                key={index}
                                                                variant={overduePage === index ? "default" : "outline"}
                                                                size="sm"
                                                                className="h-8 w-8"
                                                                onClick={() => handleOverduePageChange(index)}
                                                            >
                                                                {index + 1}
                                                            </Button>
                                                        ))
                                                        .slice(
                                                            Math.max(0, overduePage - 1),
                                                            Math.min(
                                                                overduePage + 2,
                                                                Math.ceil(filteredOverdueTasksForTable.length / overdueRowsPerPage)
                                                            )
                                                        )}
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        onClick={() => handleOverduePageChange(overduePage + 1)}
                                                        disabled={
                                                            overduePage >= Math.ceil(filteredOverdueTasksForTable.length / overdueRowsPerPage) - 1
                                                        }
                                                    >
                                                        <ChevronRight className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </>
                            ) : (
                                <>
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                                    <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                                                </div>
                                                <div>
                                                    <CardTitle className="text-lg font-semibold text-green-800 dark:text-green-200">
                                                        All Caught Up! 🎉
                                                    </CardTitle>
                                                    <CardDescription className="text-green-600 dark:text-green-400">
                                                        ✨ No overdue tasks for the selected time period and filters
                                                    </CardDescription>
                                                </div>
                                            </div>
                                            <Badge variant="default" className="text-sm font-medium bg-green-600 hover:bg-green-700">
                                                On Track
                                            </Badge>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-center py-12">
                                            <div className="flex flex-col items-center space-y-4">
                                                <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full">
                                                    <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
                                                </div>
                                                <div className="space-y-2">
                                                    <h3 className="text-xl font-semibold text-green-800 dark:text-green-200">
                                                        Outstanding Performance!
                                                    </h3>
                                                    <p className="text-green-600 dark:text-green-400 max-w-md">
                                                        Your team is staying on top of all responsibilities and meeting deadlines consistently 
                                                        for the selected time period. This level of organization and accountability is truly commendable!
                                                    </p>
                                                </div>
                                                <div className="flex items-center space-x-2 text-sm text-green-600 dark:text-green-400">
                                                    <CheckCircle2 className="h-4 w-4" />
                                                    <span>All tasks are up to date for current filters</span>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </>
                            )}
                        </Card>
                        <div className="space-y-4">
                                <div className="grid grid-cols-1 gap-4">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>
                                                Team Task Completion
                                            </CardTitle>
                                            <CardDescription>
                                                Active tasks and completion rate by
                                                team member
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="px-0">
                                            {(() => {
                                                const graphData = selectedTeamMembers.length > 0 
                                                    ? teamMembersData.filter(member => selectedTeamMembers.includes(member.id))
                                                    : teamMembersData;
                                                
                                                // Filter out members with no active or completed tasks to avoid empty bars
                                                const filteredGraphData = graphData.filter(member => 
                                                    (member.tasks?.remaining || 0) > 0 || (member.tasks?.completed || 0) > 0
                                                );
                                                
                                                if (filteredGraphData.length === 0) {
                                                    return (
                                                        <div className="flex flex-col items-center justify-center h-[350px] text-center p-8">
                                                            <div className="w-16 h-16 bg-muted/20 rounded-full flex items-center justify-center mb-4">
                                                                <BarChart3 className="w-8 h-8 text-muted-foreground" />
                                                            </div>
                                                            <h3 className="text-lg font-semibold text-foreground mb-2">No Data Available</h3>
                                                            <p className="text-sm text-muted-foreground max-w-sm">
                                                                No active or completed tasks found for the selected team members and date range.
                                                            </p>
                                                        </div>
                                                    );
                                                }
                                                
                                                // Calculate dynamic height based on number of employees
                                                // Base height: ~50px (for margins, legend, etc.)
                                                // Per employee: ~28px (bar height + spacing)
                                                // Minimum: 200px, Maximum: 800px
                                                const dynamicHeight = Math.min(800, Math.max(200, 50 + (filteredGraphData.length * 28)));
                                                
                                                // Calculate dynamic font size based on number of employees
                                                // More employees = smaller font, fewer employees = larger font
                                                // Range: 10px (many employees) to 14px (single employee)
                                                const dynamicFontSize = Math.max(10, Math.min(14, 14 - Math.floor(filteredGraphData.length / 2)));
                                                
                                                return (
                                                    <ResponsiveContainer
                                                        width="100%"
                                                        height={dynamicHeight}
                                                    >
                                                        <BarChart
                                                            data={filteredGraphData}
                                                            layout="vertical"
                                                            margin={{
                                                                top: 20,
                                                                right: 30,
                                                                left: 5,
                                                                bottom: 5,
                                                            }}
                                                        >
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis type="number" />
                                                            <YAxis
                                                                dataKey="displayName"
                                                                type="category"
                                                                tick={{ fontSize: dynamicFontSize }}
                                                                width={100}
                                                            />
                                                            <Tooltip />
                                                            <Legend />
                                                            <Bar
                                                                dataKey="tasks.completed"
                                                                stackId="a"
                                                                fill="#10b981"
                                                                name="Completed"
                                                            />
                                                            <Bar
                                                                dataKey="tasks.remaining"
                                                                stackId="a"
                                                                fill="#94a3b8"
                                                                name="Active"
                                                            />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                );
                                            })()}
                                        </CardContent>
                                    </Card>

                                    {/* <Card>
                                        <CardHeader>
                                            <CardTitle>
                                                Recent Team Activity
                                            </CardTitle>
                                            <CardDescription>
                                                Latest actions by team members
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-4">
                                                {recentActivity.length > 0 ? (
                                                    recentActivity.map((activity) => {
                                                        const Icon = activity.icon;
                                                        return (
                                                            <div
                                                                key={activity.id}
                                                                className="flex items-start gap-4"
                                                            >
                                                                <div className="rounded-full bg-muted p-2">
                                                                    <Icon className="h-4 w-4" />
                                                                </div>
                                                                <div className="flex-1 space-y-1">
                                                                    <p className="text-sm font-medium leading-none">
                                                                        <span className="font-semibold">
                                                                            {activity.user}
                                                                        </span>{" "}
                                                                        {activity.title.replace(/^(Completed|Created) task: /, '')}
                                                                    </p>
                                                                    <p className="text-xs text-muted-foreground">
                                                                        {getTimeAgo(activity.timestamp)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <p className="text-sm text-muted-foreground text-center py-4">
                                                        No recent team activity
                                                    </p>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card> */}
                                </div>
                        </div>

                    </>
                )}
                {/* Tasks Due Today Modal */}
                {showTasksDueToday && (
                    <Dialog
                        open={showTasksDueToday}
                        onOpenChange={setShowTasksDueToday}
                    >
                        <DialogContent className="max-w-xs sm:max-w-lg lg:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col p-0">
                            <DialogHeader className="p-6 pb-2">
                                <DialogTitle>{getTasksDueTodayCardInfo().title}</DialogTitle>
                                <DialogDescription>
                                    {getTasksDueTodayCardInfo().description}
                                </DialogDescription>
                            </DialogHeader>
                            
                            {/* Search and Filter Controls */}
                            <div className="px-6 py-4 border-b">
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                                            <Input
                                                placeholder="Search tasks..."
                                                value={tasksDueTodaySearchTerm}
                                                onChange={(e) => setTasksDueTodaySearchTerm(e.target.value)}
                                                className="pl-10"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Select
                                            value={tasksDueTodayStatusFilter}
                                            onValueChange={setTasksDueTodayStatusFilter}
                                        >
                                            <SelectTrigger className="w-[120px]">
                                                <SelectValue placeholder="Status" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="All">All Status</SelectItem>
                                                {statuses.map((status) => (
                                                    <SelectItem key={status} value={status}>
                                                        {status}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Select
                                            value={tasksDueTodayPriorityFilter}
                                            onValueChange={setTasksDueTodayPriorityFilter}
                                        >
                                            <SelectTrigger className="w-[120px]">
                                                <SelectValue placeholder="Priority" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="All">All Priority</SelectItem>
                                                <SelectItem value="1">Low (1)</SelectItem>
                                                <SelectItem value="5">Medium (5)</SelectItem>
                                                <SelectItem value="8">High (8)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            <div className="flex-1 overflow-auto px-6 py-2">
                                {filteredTasksDueToday.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="flex flex-col items-center space-y-4">
                                            <div className="p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                                                <Clock className="h-12 w-12 text-yellow-600 dark:text-yellow-400" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold">No tasks found</h3>
                                                <p className="text-muted-foreground">
                                                    {tasksDueTodaySearchTerm || tasksDueTodayStatusFilter !== "All" || tasksDueTodayPriorityFilter !== "All"
                                                        ? "Try adjusting your search or filter criteria."
                                                        : "You have no tasks due today or past due."}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : isMobile ? (
                                    // Card view for mobile
                                    <div className="space-y-2">
                                        {filteredTasksDueToday.map((task) => (
                                            <div
                                                key={task.id}
                                                className="border rounded-lg p-3 mb-3 bg-card cursor-pointer hover:bg-muted/50 transition-colors"
                                                onClick={() => {
                                                    setSelectedTaskForDetail(task);
                                                    setShowTaskDetailModal(true);
                                                }}
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <h3 className="font-medium text-sm">{task.name}</h3>
                                                        <p className="text-xs text-muted-foreground">{task.id}</p>
                                                    </div>
                                                    <Badge
                                                        variant={
                                                            task.priority === "High" ? "destructive" : "secondary"
                                                        }
                                                        className="ml-2"
                                                    >
                                                        {task.priority}
                                                    </Badge>
                                                </div>
                                                <div className="flex justify-between text-xs mt-2">
                                                    <span className="px-2 py-1 bg-muted rounded-md">
                                                        {task.status}
                                                    </span>
                                                    <span className="text-muted-foreground">
                                                        {formatTaskDate(task)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    // Table view for larger screens
                                    <div className="rounded-md border">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-[90px]">
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (tasksDueTodaySortField === "id") {
                                                                    setTasksDueTodaySortDirection(tasksDueTodaySortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setTasksDueTodaySortField("id");
                                                                    setTasksDueTodaySortDirection("asc");
                                                                }
                                                            }}
                                                        >
                                                            ID
                                                            {tasksDueTodaySortField === "id" && (
                                                                tasksDueTodaySortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                    <TableHead>
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (tasksDueTodaySortField === "name") {
                                                                    setTasksDueTodaySortDirection(tasksDueTodaySortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setTasksDueTodaySortField("name");
                                                                    setTasksDueTodaySortDirection("asc");
                                                                }
                                                            }}
                                                        >
                                                            Task
                                                            {tasksDueTodaySortField === "name" && (
                                                                tasksDueTodaySortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                    {!isTablet && (
                                                        <TableHead className="w-[100px]">
                                                            <Button
                                                                variant="ghost"
                                                                className="h-auto p-0 font-semibold"
                                                                onClick={() => {
                                                                    if (tasksDueTodaySortField === "priority") {
                                                                        setTasksDueTodaySortDirection(tasksDueTodaySortDirection === "asc" ? "desc" : "asc");
                                                                    } else {
                                                                        setTasksDueTodaySortField("priority");
                                                                        setTasksDueTodaySortDirection("asc");
                                                                    }
                                                                }}
                                                            >
                                                                Priority
                                                                {tasksDueTodaySortField === "priority" && (
                                                                    tasksDueTodaySortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                                )}
                                                            </Button>
                                                        </TableHead>
                                                    )}
                                                    <TableHead className="w-[100px]">
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (tasksDueTodaySortField === "status") {
                                                                    setTasksDueTodaySortDirection(tasksDueTodaySortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setTasksDueTodaySortField("status");
                                                                    setTasksDueTodaySortDirection("asc");
                                                                }
                                                            }}
                                                        >
                                                            Status
                                                            {tasksDueTodaySortField === "status" && (
                                                                tasksDueTodaySortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                    <TableHead className="text-right w-[120px]">
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (tasksDueTodaySortField === "dueDate") {
                                                                    setTasksDueTodaySortDirection(tasksDueTodaySortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setTasksDueTodaySortField("dueDate");
                                                                    setTasksDueTodaySortDirection("asc");
                                                                }
                                                            }}
                                                        >
                                                            Due
                                                            {tasksDueTodaySortField === "dueDate" && (
                                                                tasksDueTodaySortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {filteredTasksDueToday.map((task) => {
                                                    // Check if task is overdue
                                                    const taskDate = getTaskDueDate(task);
                                                    const today = new Date();
                                                    today.setHours(0, 0, 0, 0);
                                                    const isOverdue = taskDate && taskDate < today && task.status !== "Completed";
                                                    
                                                    return (
                                                    <TableRow 
                                                        key={task.id}
                                                            className={`cursor-pointer ${isOverdue ? 'bg-red-50 dark:bg-red-900/50 border-l-4 border-l-red-500 hover:bg-red-100 dark:hover:bg-red-900/35' : 'hover:bg-muted/50'}`}
                                                        onClick={() => {
                                                            setSelectedTaskForDetail(task);
                                                            setShowTaskDetailModal(true);
                                                        }}
                                                    >
                                                        <TableCell className="font-medium text-xs">
                                                            {task.id}
                                                        </TableCell>
                                                        <TableCell className="font-medium">
                                                            {task.name}
                                                        </TableCell>
                                                        {!isTablet && (
                                                            <TableCell>
                                                                <Badge
                                                                    variant={
                                                                        task.priority ===
                                                                        "High"
                                                                            ? "destructive"
                                                                            : "secondary"
                                                                    }
                                                                >
                                                                    {
                                                                        task.priority
                                                                    }
                                                                </Badge>
                                                            </TableCell>
                                                        )}
                                                        <TableCell>
                                                            {task.status}
                                                        </TableCell>
                                                        <TableCell className={`text-right ${isOverdue ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>
                                                            {formatTaskDate(task)}
                                                        </TableCell>
                                                    </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                            <DialogFooter className="p-6 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowTasksDueToday(false)}
                                >
                                    Close
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}

                {/* Overdue Tasks Modal */}
                {showOverdueTasks && (
                    <Dialog
                        open={showOverdueTasks}
                        onOpenChange={setShowOverdueTasks}
                    >
                        <DialogContent className="max-w-xs sm:max-w-lg lg:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col p-0">
                            <DialogHeader className="p-6 pb-2">
                                <DialogTitle>Overdue Tasks</DialogTitle>
                                <DialogDescription>
                                    Here are all your overdue tasks that need immediate attention.
                                </DialogDescription>
                            </DialogHeader>
                            
                            {/* Search and Filter Controls */}
                            <div className="px-6 py-4 border-b">
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                                            <Input
                                                placeholder="Search overdue tasks..."
                                                value={overdueTasksSearchTerm}
                                                onChange={(e) => setOverdueTasksSearchTerm(e.target.value)}
                                                className="pl-10"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex-1 overflow-auto px-6 py-2">
                                {filteredOverdueTasks.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="flex flex-col items-center space-y-4">
                                            <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-full">
                                                <AlertTriangle className="h-12 w-12 text-red-600 dark:text-red-400" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold">No overdue tasks found</h3>
                                                <p className="text-muted-foreground">
                                                    {overdueTasksSearchTerm
                                                        ? "Try adjusting your search criteria."
                                                        : "Great! You have no overdue tasks."}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : isMobile ? (
                                    // Card view for mobile
                                    <div className="space-y-2">
                                        {filteredOverdueTasks.map((task) => (
                                            <div
                                                key={task.id}
                                                className="border-l-4 border-red-500 rounded-lg p-3 mb-3 bg-red-50/30 dark:bg-red-900/50 cursor-pointer hover:bg-red-50/50 dark:hover:bg-red-900/35 transition-colors"
                                                onClick={() => {
                                                    setSelectedTaskForDetail(task);
                                                    setShowTaskDetailModal(true);
                                                }}
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <h3 className="font-medium text-sm">{task.name}</h3>
                                                        <p className="text-xs text-muted-foreground">{task.id}</p>
                                                    </div>
                                                    <Badge
                                                        variant="outline"
                                                        className={`ml-2 ${getPriorityColor(task.priority)}`}
                                                    >
                                                        {task.priority}
                                                    </Badge>
                                                </div>
                                                <div className="flex justify-between text-xs mt-2">
                                                    <span className="px-2 py-1 bg-muted rounded-md">
                                                        {task.status}
                                                    </span>
                                                    <span className="text-red-600 dark:text-red-400 font-medium">
                                                        {formatTaskDate(task)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    // Table view for larger screens
                                    <div className="rounded-md border">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-[90px]">
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (overdueTasksSortField === "id") {
                                                                    setOverdueTasksSortDirection(overdueTasksSortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setOverdueTasksSortField("id");
                                                                    setOverdueTasksSortDirection("asc");
                                                                }
                                                            }}
                                                        >
                                                            ID
                                                            {overdueTasksSortField === "id" && (
                                                                overdueTasksSortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                    <TableHead>
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (overdueTasksSortField === "name") {
                                                                    setOverdueTasksSortDirection(overdueTasksSortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setOverdueTasksSortField("name");
                                                                    setOverdueTasksSortDirection("asc");
                                                                }
                                                            }}
                                                        >
                                                            Task
                                                            {overdueTasksSortField === "name" && (
                                                                overdueTasksSortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                    {!isTablet && (
                                                        <TableHead className="w-[100px]">
                                                            <Button
                                                                variant="ghost"
                                                                className="h-auto p-0 font-semibold"
                                                                onClick={() => {
                                                                    if (overdueTasksSortField === "priority") {
                                                                        setOverdueTasksSortDirection(overdueTasksSortDirection === "asc" ? "desc" : "asc");
                                                                    } else {
                                                                        setOverdueTasksSortField("priority");
                                                                        setOverdueTasksSortDirection("asc");
                                                                    }
                                                                }}
                                                            >
                                                                Priority
                                                                {overdueTasksSortField === "priority" && (
                                                                    overdueTasksSortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                                )}
                                                            </Button>
                                                        </TableHead>
                                                    )}
                                                    <TableHead className="w-[100px]">
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (overdueTasksSortField === "status") {
                                                                    setOverdueTasksSortDirection(overdueTasksSortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setOverdueTasksSortField("status");
                                                                    setOverdueTasksSortDirection("asc");
                                                                }
                                                            }}
                                                        >
                                                            Status
                                                            {overdueTasksSortField === "status" && (
                                                                overdueTasksSortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                    <TableHead className="text-right w-[180px]">
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (overdueTasksSortField === "dueDate") {
                                                                    setOverdueTasksSortDirection(overdueTasksSortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setOverdueTasksSortField("dueDate");
                                                                    setOverdueTasksSortDirection("asc");
                                                                }
                                                            }}
                                                        >
                                                            Due Date
                                                            {overdueTasksSortField === "dueDate" && (
                                                                overdueTasksSortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {filteredOverdueTasks.map((task) => (
                                                    <TableRow
                                                        key={task.id}
                                                        className="bg-red-50 dark:bg-red-900/50 border-l-4 border-l-red-500 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/35"
                                                        onClick={() => {
                                                            setSelectedTaskForDetail(task);
                                                            setShowTaskDetailModal(true);
                                                        }}
                                                    >
                                                        <TableCell className="font-medium text-xs">
                                                            {task.id}
                                                        </TableCell>
                                                        <TableCell className="font-medium">
                                                            {task.name}
                                                        </TableCell>
                                                        {!isTablet && (
                                                            <TableCell>
                                                                <Badge
                                                                    variant="outline"
                                                                    className={getPriorityColor(task.priority)}
                                                                >
                                                                    {task.priority}
                                                                </Badge>
                                                            </TableCell>
                                                        )}
                                                        <TableCell>
                                                            {task.status}
                                                        </TableCell>
                                                        <TableCell className="text-right text-red-600 dark:text-red-400 font-medium">
                                                            {formatTaskDate(task)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                            <DialogFooter className="p-6 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowOverdueTasks(false)}
                                >
                                    Close
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}

                {/* Completed Tasks Modal */}
                {showCompletedTasks && (
                    <Dialog
                        open={showCompletedTasks}
                        onOpenChange={setShowCompletedTasks}
                    >
                        <DialogContent className="max-w-xs sm:max-w-lg lg:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col p-0">
                            <DialogHeader className="p-6 pb-2">
                                <DialogTitle>{getCompletedTasksCardInfo().title}</DialogTitle>
                                <DialogDescription>
                                    {getCompletedTasksCardInfo().description}
                                </DialogDescription>
                            </DialogHeader>
                            
                            {/* Search and Filter Controls */}
                            <div className="px-6 py-4 border-b">
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                                            <Input
                                                placeholder="Search completed tasks..."
                                                value={completedTasksSearchTerm}
                                                onChange={(e) => setCompletedTasksSearchTerm(e.target.value)}
                                                className="pl-10"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Select
                                            value={completedTasksStatusFilter}
                                            onValueChange={setCompletedTasksStatusFilter}
                                        >
                                            <SelectTrigger className="w-[120px]">
                                                <SelectValue placeholder="Status" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="All">All Status</SelectItem>
                                                {statuses.map((status) => (
                                                    <SelectItem key={status} value={status}>
                                                        {status}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Select
                                            value={completedTasksPriorityFilter}
                                            onValueChange={setCompletedTasksPriorityFilter}
                                        >
                                            <SelectTrigger className="w-[120px]">
                                                <SelectValue placeholder="Priority" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="All">All Priority</SelectItem>
                                                <SelectItem value="1">Low (1)</SelectItem>
                                                <SelectItem value="5">Medium (5)</SelectItem>
                                                <SelectItem value="8">High (8)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            <div className="flex-1 overflow-auto px-6 py-2">
                                {filteredCompletedTasks.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="flex flex-col items-center space-y-4">
                                            <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full">
                                                <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold">No completed tasks found</h3>
                                                <p className="text-muted-foreground">
                                                    {completedTasksSearchTerm || completedTasksStatusFilter !== "All" || completedTasksPriorityFilter !== "All"
                                                        ? "Try adjusting your search or filter criteria."
                                                        : "You haven't completed any tasks yet."}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : isMobile ? (
                                    // Card view for mobile
                                    <div className="space-y-2">
                                        {filteredCompletedTasks.map((task) => (
                                            <div
                                                key={task.id}
                                                className="border rounded-lg p-3 mb-3 bg-card cursor-pointer hover:bg-muted/50 transition-colors"
                                                onClick={() => {
                                                    setSelectedTaskForDetail(task);
                                                    setShowTaskDetailModal(true);
                                                }}
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <h3 className="font-medium text-sm">{task.name}</h3>
                                                        <p className="text-xs text-muted-foreground">{task.id}</p>
                                                    </div>
                                                    <Badge
                                                        variant="outline"
                                                        className={`ml-2 ${getPriorityColor(task.priority)}`}
                                                    >
                                                        {task.priority}
                                                    </Badge>
                                                </div>
                                                <div className="flex justify-between text-xs mt-2">
                                                    <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-md">
                                                        Completed
                                                    </span>
                                                    <span className="text-muted-foreground">
                                                        {task.completed_date ? format(new Date(task.completed_date), "MMM dd, yyyy") : "No date"}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    // Table view for larger screens
                                    <div className="rounded-md border">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-[90px]">
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (completedTasksSortField === "id") {
                                                                    setCompletedTasksSortDirection(completedTasksSortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setCompletedTasksSortField("id");
                                                                    setCompletedTasksSortDirection("asc");
                                                                }
                                                            }}
                                                        >
                                                            ID
                                                            {completedTasksSortField === "id" && (
                                                                completedTasksSortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                    <TableHead>
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (completedTasksSortField === "name") {
                                                                    setCompletedTasksSortDirection(completedTasksSortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setCompletedTasksSortField("name");
                                                                    setCompletedTasksSortDirection("asc");
                                                                }
                                                            }}
                                                        >
                                                            Task
                                                            {completedTasksSortField === "name" && (
                                                                completedTasksSortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                    {!isTablet && (
                                                        <TableHead className="w-[100px]">
                                                            <Button
                                                                variant="ghost"
                                                                className="h-auto p-0 font-semibold"
                                                                onClick={() => {
                                                                    if (completedTasksSortField === "priority") {
                                                                        setCompletedTasksSortDirection(completedTasksSortDirection === "asc" ? "desc" : "asc");
                                                                    } else {
                                                                        setCompletedTasksSortField("priority");
                                                                        setCompletedTasksSortDirection("asc");
                                                                    }
                                                                }}
                                                            >
                                                                Priority
                                                                {completedTasksSortField === "priority" && (
                                                                    completedTasksSortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                                )}
                                                            </Button>
                                                        </TableHead>
                                                    )}
                                                    <TableHead className="w-[100px]">
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (completedTasksSortField === "status") {
                                                                    setCompletedTasksSortDirection(completedTasksSortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setCompletedTasksSortField("status");
                                                                    setCompletedTasksSortDirection("asc");
                                                                }
                                                            }}
                                                        >
                                                            Status
                                                            {completedTasksSortField === "status" && (
                                                                completedTasksSortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                    <TableHead className="text-right w-[140px]">
                                                        <Button
                                                            variant="ghost"
                                                            className="h-auto p-0 font-semibold"
                                                            onClick={() => {
                                                                if (completedTasksSortField === "completedDate") {
                                                                    setCompletedTasksSortDirection(completedTasksSortDirection === "asc" ? "desc" : "asc");
                                                                } else {
                                                                    setCompletedTasksSortField("completedDate");
                                                                    setCompletedTasksSortDirection("desc");
                                                                }
                                                            }}
                                                        >
                                                            Completed
                                                            {completedTasksSortField === "completedDate" && (
                                                                completedTasksSortDirection === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />
                                                            )}
                                                        </Button>
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {filteredCompletedTasks.map((task) => (
                                                    <TableRow 
                                                        key={task.id}
                                                        className="cursor-pointer hover:bg-muted/50"
                                                        onClick={() => {
                                                            setSelectedTaskForDetail(task);
                                                            setShowTaskDetailModal(true);
                                                        }}
                                                    >
                                                        <TableCell className="font-medium text-xs">
                                                            {task.id}
                                                        </TableCell>
                                                        <TableCell className="font-medium">
                                                            {task.name}
                                                        </TableCell>
                                                        {!isTablet && (
                                                            <TableCell>
                                                                <Badge
                                                                    variant="outline"
                                                                    className={getPriorityColor(task.priority)}
                                                                >
                                                                    {task.priority}
                                                                </Badge>
                                                            </TableCell>
                                                        )}
                                                        <TableCell>
                                                            <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-md text-xs">
                                                                Completed
                                                            </span>
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {task.completed_date ? format(new Date(task.completed_date), "MMM dd, yyyy") : "No date"}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                            <DialogFooter className="p-6 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowCompletedTasks(false)}
                                >
                                    Close
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}

                {/* Active Workflows Modal */}
                {showActiveWorkflows && (
                    <Dialog
                        open={showActiveWorkflows}
                        onOpenChange={setShowActiveWorkflows}
                    >
                        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col p-0">
                            <DialogHeader className="p-6 pb-2">
                                <DialogTitle>Active Workflows</DialogTitle>
                                <DialogDescription>
                                    Here are all your active workflows.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="flex-1 overflow-auto px-6 py-2">
                                {isMobile ? (
                                    // Card view for mobile
                                    <div className="space-y-2">
                                        {activeWorkflows.map((workflow) => {
                                            const trigger = getWorkflowTrigger(workflow);
                                            return (
                                                <div
                                                    key={workflow.id}
                                                    className="border rounded-lg p-3 mb-3 bg-card"
                                                >
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div>
                                                            <h3 className="font-medium text-sm">{workflow.name}</h3>
                                                            <p className="text-xs text-muted-foreground">
                                                                {workflow.id}
                                                            </p>
                                                        </div>
                                                        {getWorkflowStatusBadge(workflow.status)}
                                                    </div>
                                                    <div className="space-y-2 mt-2">
                                                        <div className="flex justify-between text-xs">
                                                            <span className="text-muted-foreground">Trigger:</span>
                                                            <span className="capitalize">{trigger.display}</span>
                                                        </div>
                                                        <div className="flex justify-between text-xs">
                                                            <span className="text-muted-foreground">Last run:</span>
                                                            <span>
                                                                {workflow.lastRun
                                                                    ? format(
                                                                          new Date(workflow.lastRun),
                                                                          "MMM dd, h:mm a"
                                                                      )
                                                                    : "Never"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    // Table view for larger screens
                                    <div className="rounded-md border">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-[90px]">
                                                        ID
                                                    </TableHead>
                                                    <TableHead>
                                                        Workflow
                                                    </TableHead>
                                                    <TableHead className="w-[100px]">
                                                        Status
                                                    </TableHead>
                                                    {!isTablet && (
                                                        <TableHead className="w-[140px]">
                                                            Trigger
                                                        </TableHead>
                                                    )}
                                                    <TableHead className="text-right w-[140px]">
                                                        Last Run
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {activeWorkflows.map((workflow) => {
                                                    const trigger = getWorkflowTrigger(workflow);
                                                    return (
                                                        <TableRow key={workflow.id}>
                                                            <TableCell className="font-medium text-xs">
                                                                {workflow.id}
                                                            </TableCell>
                                                            <TableCell className="font-medium">
                                                                {workflow.name}
                                                            </TableCell>
                                                            <TableCell>
                                                                {getWorkflowStatusBadge(workflow.status)}
                                                            </TableCell>
                                                            {!isTablet && (
                                                                <TableCell>
                                                                    <div className="flex items-center">
                                                                        <span className="capitalize">{trigger.display}</span>
                                                                    </div>
                                                                </TableCell>
                                                            )}
                                                            <TableCell className="text-right">
                                                                {workflow.lastRun
                                                                    ? format(
                                                                          new Date(workflow.lastRun),
                                                                          "MMM dd, h:mm a"
                                                                      )
                                                                    : "Never"}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                            <DialogFooter className="p-6 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={() =>
                                        setShowActiveWorkflows(false)
                                    }
                                >
                                    Close
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}

                {/* Task Completion Confirmation Dialog */}
                {showCompleteTaskDialog && (
                    <Dialog
                        open={showCompleteTaskDialog}
                        onOpenChange={setShowCompleteTaskDialog}
                    >
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Complete Task</DialogTitle>
                                <DialogDescription>
                                    Are you sure you want to mark this task as completed? 
                                    {taskToComplete?.users && taskToComplete.users.length > 0 && (
                                        <span className="block mt-2 text-sm">
                                            An email notification will be sent to: {taskToComplete.users.map(user => user.name).join(', ')}
                                        </span>
                                    )}
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => setShowCompleteTaskDialog(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={confirmCompleteTask}
                                >
                                    Complete Task
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}

                {/* Task Detail Modal */}
                <TaskDetailModal
                    open={showTaskDetailModal}
                    onOpenChange={setShowTaskDetailModal}
                    task={selectedTaskForDetail}
                    onComplete={handleTaskDetailComplete}
                    onDefer={handleTaskDetailDefer}
                    onAddNote={handleTaskDetailAddNote}
                    onUpdateStatus={handleTaskDetailStatusUpdate}
                    isSubmitting={false}
                    onViewParent={handleTaskDetailViewParent}
                    availableWorkflows={workflows}
                    users={users}
                    categories={categories}
                />

                {/* Active Tasks Modal */}
                <Dialog
                    open={showActiveTasksModal}
                    onOpenChange={setShowActiveTasksModal}
                >
                    <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col p-0">
                        <DialogHeader className="p-6 pb-2">
                            <DialogTitle>Active Tasks</DialogTitle>
                            <DialogDescription>
                                {getActiveTasksLabel()} - Tasks that are in progress or to do
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex-1 overflow-auto px-6 py-2">
                            {/* Filters */}
                            <div className="mb-4 space-y-4">
                                <div className="flex flex-col sm:flex-row gap-4">
                                    {/* Search */}
                                    <div className="flex-1">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                                            <Input
                                                placeholder="Search tasks, IDs, or assignees..."
                                                value={activeTasksSearchTerm}
                                                onChange={(e) => setActiveTasksSearchTerm(e.target.value)}
                                                className="pl-10"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Status Filter */}
                                    <Select value={activeTasksStatusFilter} onValueChange={setActiveTasksStatusFilter}>
                                        <SelectTrigger className="w-[140px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="All">All Statuses</SelectItem>
                                            <SelectItem value="To Do">To Do</SelectItem>
                                            <SelectItem value="In Progress">In Progress</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    
                                    {/* Priority Filter */}
                                    <Select value={activeTasksPriorityFilter} onValueChange={setActiveTasksPriorityFilter}>
                                        <SelectTrigger className="w-[140px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="All">All Priorities</SelectItem>
                                            <SelectItem value="10">10 (Highest)</SelectItem>
                                            <SelectItem value="9">9</SelectItem>
                                            <SelectItem value="8">8</SelectItem>
                                            <SelectItem value="7">7</SelectItem>
                                            <SelectItem value="6">6</SelectItem>
                                            <SelectItem value="5">5</SelectItem>
                                            <SelectItem value="4">4</SelectItem>
                                            <SelectItem value="3">3</SelectItem>
                                            <SelectItem value="2">2</SelectItem>
                                            <SelectItem value="1">1 (Lowest)</SelectItem>
                                        </SelectContent>
                                    </Select>

                                    {/* Clear Filters Button */}
                                    {(activeTasksSearchTerm || activeTasksStatusFilter !== "All" || activeTasksPriorityFilter !== "All") && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setActiveTasksSearchTerm("");
                                                setActiveTasksStatusFilter("All");
                                                setActiveTasksPriorityFilter("All");
                                                setActiveTasksPage(0);
                                            }}
                                        >
                                            Clear Filters
                                        </Button>
                                    )}
                                </div>
                            </div>
                            
                            {/* Tasks Table */}
                            <div className="rounded-md border overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[90px]">ID</TableHead>
                                            <TableHead>Task</TableHead>
                                            <TableHead>Assignee</TableHead>
                                            {!isTablet && (
                                                <TableHead className="w-[100px]">Priority</TableHead>
                                            )}
                                            <TableHead className="w-[100px]">Status</TableHead>
                                            <TableHead className="text-right w-[120px]">Due Date</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {paginatedActiveTasks.length > 0 ? (
                                            paginatedActiveTasks.map((task) => {
                                                // Check if task is overdue
                                                const taskDate = getTaskDueDate(task);
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                const taskDateNormalized = taskDate ? new Date(taskDate) : null;
                                                if (taskDateNormalized) {
                                                    taskDateNormalized.setHours(0, 0, 0, 0);
                                                }
                                                const isOverdue = taskDateNormalized && taskDateNormalized < today && task.status !== 'Completed';
                                                
                                                return (
                                                <TableRow 
                                                    key={task.id}
                                                    className={`cursor-pointer ${
                                                        isOverdue 
                                                            ? 'bg-red-50 dark:bg-red-900/50 border-l-4 border-l-red-500 hover:bg-red-100 dark:hover:bg-red-900/35' 
                                                            : 'hover:bg-muted/50'
                                                    }`}
                                                    onClick={() => handleOpenTaskDetail(task)}
                                                >
                                                    <TableCell className="font-medium text-xs">{task.id}</TableCell>
                                                    <TableCell className="font-medium">{task.name}</TableCell>
                                                    <TableCell>{task.users?.map(user => user.name).join(', ') || 'Unassigned'}</TableCell>
                                                    {!isTablet && (
                                                        <TableCell>
                                                            <Badge
                                                                variant="outline"
                                                                className={`ml-2 ${getPriorityColor(task.priority)}`}
                                                            >
                                                                {task.priority}
                                                            </Badge>
                                                        </TableCell>
                                                    )}
                                                    <TableCell>
                                                        <Badge
                                                            variant="outline"
                                                            className={getStatusColor(task.status)}
                                                        >
                                                            {task.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-right font-medium">{formatTaskDate(task)}</TableCell>
                                                </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                    No active tasks match the current filters
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                            
                            {/* Pagination for active tasks */}
                            {filteredActiveTasks.length > 0 && (
                                <div className="flex items-center justify-between p-4 mt-4 bg-card rounded-md border">
                                    <div className="flex items-center space-x-2">
                                        <Select
                                            value={activeTasksRowsPerPage.toString()}
                                            onValueChange={(value) => {
                                                setActiveTasksRowsPerPage(Number(value));
                                                setActiveTasksPage(0);
                                            }}
                                        >
                                            <SelectTrigger className="w-[100px] h-8">
                                                <SelectValue placeholder="Rows" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="10">10 rows</SelectItem>
                                                <SelectItem value="20">20 rows</SelectItem>
                                                <SelectItem value="50">50 rows</SelectItem>
                                                <SelectItem value="100">100 rows</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <div className="text-sm text-muted-foreground">
                                            Showing <strong>{paginatedActiveTasks.length}</strong> of{" "}
                                            <strong>{filteredActiveTasks.length}</strong> active tasks
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleActiveTasksPageChange(activeTasksPage - 1)}
                                            disabled={activeTasksPage === 0}
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        {Array.from({
                                            length: Math.ceil(filteredActiveTasks.length / activeTasksRowsPerPage),
                                        })
                                            .map((_, index) => (
                                                <Button
                                                    key={index}
                                                    variant={activeTasksPage === index ? "default" : "outline"}
                                                    size="sm"
                                                    className="h-8 w-8"
                                                    onClick={() => handleActiveTasksPageChange(index)}
                                                >
                                                    {index + 1}
                                                </Button>
                                            ))
                                            .slice(
                                                Math.max(0, activeTasksPage - 1),
                                                Math.min(
                                                    activeTasksPage + 2,
                                                    Math.ceil(filteredActiveTasks.length / activeTasksRowsPerPage)
                                                )
                                            )}
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleActiveTasksPageChange(activeTasksPage + 1)}
                                            disabled={
                                                activeTasksPage >= Math.ceil(filteredActiveTasks.length / activeTasksRowsPerPage) - 1
                                            }
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <DialogFooter className="p-6 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowActiveTasksModal(false)}
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* All Tasks Modal (for completion rate card) */}
                <Dialog
                    open={showAllTasksModal}
                    onOpenChange={setShowAllTasksModal}
                >
                    <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col p-0">
                        <DialogHeader className="p-6 pb-2">
                            <DialogTitle>All Tasks</DialogTitle>
                            <DialogDescription>
                                View all tasks (completed, overdue, to do, in progress) for the selected time period and filters
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex-1 overflow-auto px-6 py-2">
                            {/* Filters */}
                            <div className="mb-4 space-y-4">
                                <div className="flex flex-col sm:flex-row gap-4">
                                    {/* Search */}
                                    <div className="flex-1">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                                            <Input
                                                placeholder="Search tasks, IDs, or assignees..."
                                                value={allTasksSearchTerm}
                                                onChange={(e) => setAllTasksSearchTerm(e.target.value)}
                                                className="pl-10"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Status Filter */}
                                    <Select value={allTasksStatusFilter} onValueChange={setAllTasksStatusFilter}>
                                        <SelectTrigger className="w-[140px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="All">All Statuses</SelectItem>
                                            <SelectItem value="To Do">To Do</SelectItem>
                                            <SelectItem value="In Progress">In Progress</SelectItem>
                                            <SelectItem value="Completed">Completed</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    
                                    {/* Priority Filter */}
                                    <Select value={allTasksPriorityFilter} onValueChange={setAllTasksPriorityFilter}>
                                        <SelectTrigger className="w-[140px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="All">All Priorities</SelectItem>
                                            <SelectItem value="10">10 (Highest)</SelectItem>
                                            <SelectItem value="9">9</SelectItem>
                                            <SelectItem value="8">8</SelectItem>
                                            <SelectItem value="7">7</SelectItem>
                                            <SelectItem value="6">6</SelectItem>
                                            <SelectItem value="5">5</SelectItem>
                                            <SelectItem value="4">4</SelectItem>
                                            <SelectItem value="3">3</SelectItem>
                                            <SelectItem value="2">2</SelectItem>
                                            <SelectItem value="1">1 (Lowest)</SelectItem>
                                        </SelectContent>
                                    </Select>

                                    {/* Clear Filters Button */}
                                    {(allTasksSearchTerm || allTasksStatusFilter !== "Completed" || allTasksPriorityFilter !== "All") && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setAllTasksSearchTerm("");
                                                setAllTasksStatusFilter("Completed");
                                                setAllTasksPriorityFilter("All");
                                                setAllTasksPage(0);
                                            }}
                                        >
                                            Clear Filters
                                        </Button>
                                    )}
                                </div>
                            </div>
                            
                            {/* Tasks Table */}
                            <div className="rounded-md border overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[90px]">ID</TableHead>
                                            <TableHead>Task</TableHead>
                                            <TableHead>Assignee</TableHead>
                                            {!isTablet && (
                                                <TableHead className="w-[100px]">Priority</TableHead>
                                            )}
                                            <TableHead className="w-[100px]">Status</TableHead>
                                            <TableHead className="text-right w-[120px]">Due Date</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {paginatedAllTasks.length > 0 ? (
                                            paginatedAllTasks.map((task) => {
                                                const taskDueDate = getTaskDueDate(task);
                                                // Normalize dates to start of day for proper overdue detection
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                const taskDateNormalized = taskDueDate ? new Date(taskDueDate) : null;
                                                if (taskDateNormalized) {
                                                    taskDateNormalized.setHours(0, 0, 0, 0);
                                                }
                                                const isOverdue = taskDateNormalized && taskDateNormalized < today && task.status !== 'Completed';
                                                
                                                return (
                                                    <TableRow 
                                                        key={task.id}
                                                        className={`cursor-pointer ${
                                                            isOverdue 
                                                                ? 'bg-red-50 dark:bg-red-900/50 border-l-4 border-l-red-500 hover:bg-red-100 dark:hover:bg-red-900/35' 
                                                                : 'hover:bg-muted/50'
                                                        }`}
                                                        onClick={() => handleOpenTaskDetail(task)}
                                                    >
                                                    <TableCell className="font-medium text-xs">{task.id}</TableCell>
                                                    <TableCell className="font-medium">{task.name}</TableCell>
                                                    <TableCell>{task.users?.map(user => user.name).join(', ') || 'Unassigned'}</TableCell>
                                                    {!isTablet && (
                                                        <TableCell>
                                                            <Badge
                                                                variant="outline"
                                                                className={`ml-2 ${getPriorityColor(task.priority)}`}
                                                            >
                                                                {task.priority}
                                                            </Badge>
                                                        </TableCell>
                                                    )}
                                                    <TableCell>
                                                        <Badge
                                                            variant="outline"
                                                            className={getStatusColor(task.status)}
                                                        >
                                                            {task.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-right font-medium">{formatTaskDate(task)}</TableCell>
                                                </TableRow>
                                            );
                                        })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                    No tasks match the current filters
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                            
                            {/* Pagination for all tasks */}
                            {filteredAllTasks.length > 0 && (
                                <div className="flex items-center justify-between p-4 mt-4 bg-card rounded-md border">
                                    <div className="flex items-center space-x-2">
                                        <Select
                                            value={allTasksRowsPerPage.toString()}
                                            onValueChange={(value) => {
                                                setAllTasksRowsPerPage(Number(value));
                                                setAllTasksPage(0);
                                            }}
                                        >
                                            <SelectTrigger className="w-[100px] h-8">
                                                <SelectValue placeholder="Rows" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="10">10 rows</SelectItem>
                                                <SelectItem value="20">20 rows</SelectItem>
                                                <SelectItem value="50">50 rows</SelectItem>
                                                <SelectItem value="100">100 rows</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <div className="text-sm text-muted-foreground">
                                            Showing <strong>{paginatedAllTasks.length}</strong> of{" "}
                                            <strong>{filteredAllTasks.length}</strong> tasks
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleAllTasksPageChange(allTasksPage - 1)}
                                            disabled={allTasksPage === 0}
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        {Array.from({
                                            length: Math.ceil(filteredAllTasks.length / allTasksRowsPerPage),
                                        })
                                            .map((_, index) => (
                                                <Button
                                                    key={index}
                                                    variant={allTasksPage === index ? "default" : "outline"}
                                                    size="sm"
                                                    className="h-8 w-8"
                                                    onClick={() => handleAllTasksPageChange(index)}
                                                >
                                                    {index + 1}
                                                </Button>
                                            ))
                                            .slice(
                                                Math.max(0, allTasksPage - 1),
                                                Math.min(
                                                    allTasksPage + 2,
                                                    Math.ceil(filteredAllTasks.length / allTasksRowsPerPage)
                                                )
                                            )}
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleAllTasksPageChange(allTasksPage + 1)}
                                            disabled={
                                                allTasksPage >= Math.ceil(filteredAllTasks.length / allTasksRowsPerPage) - 1
                                            }
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <DialogFooter className="p-6 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowAllTasksModal(false)}
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                </div>
            
            </TaskProvider>
        </AuthenticatedLayout>
        </>
    );
}
